
import createFetchClient from "openapi-fetch";
import type { z } from "zod";
import type { paths } from "./generated/schema.js";
import type { FilterableFields } from "./generated/filterable.generated.js";
import {
    zCluster,
    zClusterNode,
    zLinkMatrix,
    zAddClusterNodeResponse,
    zViewerAccess,
    zViewerFate,
    zAppExecAccess,
    zCtConsoleAccess,
    zCtConsoleFate,
    zCtExecAccess,
    zVmDescription,
    zVmDiskChangeTracking,
    zStatsSeries,
    zGrant,
    zGroup,
    zPermissionList,
    zRole,
    zScope,
    zTag,
    zTagAssignment,
    zUser,
    zBackupExport,
    zBatchDeleteResult,
    zBatchActionResult,
    zCatalog,
    zCatalogFile,
    zRequestCatalogFileDownloadResponse,
    zCt,
    zCephCluster,
    zDisk,
    zFabric,
    zFabricImportedNetwork,
    zFirewallGroup,
    zIpamView,
    zNetworkService,
    zNetsvcPlacement,
    zNetsvcPlacementList,
    zExportVpnProfileResponse,
    zSubnet,
    zLicense,
    zGetClusterResponse,
    zGetLicenseResponse,
    zOverlayNetwork,
    zSession,
    zSessionCreated,
    zIdentifyResult,
    zAuthRealm,
    zAuthRealmTestResult,
    zMfaMethodList,
    zMfaStatus,
    zMfaEnrollment,
    zTotpEnrollment,
    zRecoveryCodes,
    zStorage,
    zGetSystemDiagnoseResponse,
    zRequestDiagnoseBundleResponse,
    zSystemOverview,
    zSystemExplorer,
    zCredential,
    zExportCredentialPemResponse,
    zGetCredentialCrlResponse,
    zOciRegistry,
    zOciRepository,
    zOciBranch,
    zOciImage,
    zOciViewClaim,
    zApp,
    zPod,
    zPodDeleteResult,
    zCheckpoint,
    zBackupDestination,
    zVmBackup,
    zCtBackup,
    zAppBackup,
    zConfigRestoreDiff,
    zConfigRestoreUpload,
    zListStorageFindingsResponse,
    zListStorageObjectsResponse,
    zActOnStorageFindingResponse,
    zProximityRule,
    zProximityStatus,
    zEvent,
    zAuditRecord,
    zNotificationTarget,
    zNotificationRule,
    zNotificationDelivery,
    zNotificationTestResult,
    zTask,
    zVirtualMachine,
    zVmPlacement,
    zVmSpof,
    zNodeEvacuationPlan,
    zNodeFirewall,
    zReadOnlyAccess,
    zReadOnlyAccessGranted,
    zFabricNodeConfigPlan,
    zFabricNodeConfigApplyResult,
    zFabricNodeConfigRestoreResult,
} from "./generated/zod/zod.gen.js";
import { ndjsonLines } from "./ndjson.js";
import { genPageIterator, type PageIterator } from "./page-iterator.js";
import { TVirtApiError, TVirtTaskError, TVirtWaitTimeoutError } from "./problem.js";
import { TVirtResponseError } from "./response-error.js";



// The hand-written facade over the typed transport (design ratified 2026-07-10):
// structured filters serialized to the ?filter= grammar, list = one explicit page,
// listAll = per-item PageIterator, writes return { resource, task }, problem+json
// surfaces as TVirtApiError, zod validates the boundary (validateResponses).

export type FilterOp =
    | "eq" | "!eq" | "in" | "!in"
    | "contains" | "!contains" | "startsWith" | "!startsWith" | "endsWith" | "!endsWith"
    | "gt" | "gte" | "lt" | "lte" | "exists";

export interface FilterTerm<F extends string = string> {
    field: F;
    op: FilterOp;
    value: unknown;
}



export interface ListParams<F extends string = string> {
    filter?: FilterTerm<F>[];
    sort?: { field: F; direction?: "asc" | "desc" };
    scope?: string;
    // On `list`, the page size (1..500, contract default 100); on `listAll`
    // (parity R1, D1) the size of EVERY page the iterator draws — a caller
    // that stops after n items with `limit: n` pays one request.
    limit?: number;
    cursor?: string | null;
}



// Round #123 (ratified 2026-07-31): a node reference is a member NAME or a
// NodeSelector — the #106 tag → value map (entries ANDed) selecting the
// members that carry every entry. The JSON type disambiguates.
export type NodeRef = string | Record<string, string>;

// The inter-node connectivity matrix (#326), as the contract declares it —
// the facade `cluster.links()` of round hw-prep validates against zLinkMatrix.
export type LinkMatrix = z.infer<typeof zLinkMatrix>;



export interface Page<T> {
    items: T[];
    nextCursor: string | null;
    // Present (true) when the page was served from the stale replica fallback
    // (cluster-first §6) — absent on fresh store reads and on task surfaces.
    stale?: boolean;
    // Present only with `stale: true`, and only on the envelopes that publish it
    // (today the VM list — the other sixteen follow with T324-P3): WHEN the
    // observed state of the items was last taken from the store; `null` = the
    // node's state replica never reached it (T324-P6, 2026-09-04).
    stateAsOf?: string | null;
    // The items of THIS page this client could not read (round «errori onesti»,
    // 2026-08-03): one object carrying a value outside the contract this client
    // was built from no longer takes the whole list down with it — it is left
    // out of `items` and named here, so the caller can show "1 of 12 objects
    // could not be read: driver=\"nvme-tcp\"" instead of an empty page blaming
    // the network. Absent when the page validated whole.
    unreadable?: TVirtResponseError[];
}



export interface WriteResult<T> {
    resource: T;
    // The X-TVirt-Task reference — feed it to tasks.wait/follow (a parent task
    // when the operation runs as an ordered sequence of children, round #128).
    task: string | null;
}



// R6 phase 2 (2026-08-24): every config PATCH takes the optimistic precondition —
// pass the `etag` the document was read with (a readOnly field of the resource,
// LIST carries it too, so no second round trip) and a concurrent write turns
// into a speaking 412 instead of a silent lost update. `"*"` is the deliberate
// overwrite. Absent = today's behavior, unchanged.
export interface PatchOpts extends WriteOpts {
    ifMatch?: string;
}

// Parity R1 (Q10 a, 2026-11-03): every create/patch whose contract operation
// declares `DryRun` takes `{ dryRun: true }` — the server runs the FULL
// admission (schema, invariants, references, RBAC, quotas) and answers the
// object that WOULD be born or changed, with the same status and the same
// refusals, writing nothing (API spec §13; backend http/dry-run.ts). The CLI's
// `--dry-run` and the GUI's «Anteprima» are this one query parameter.
export interface WriteOpts {
    dryRun?: boolean;
}



export interface ClientOptionsV6 {
    baseUrl: string;
    // The bearer sent as `Authorization: Bearer …` on every call: a `tvts_` session
    // token (from `auth.login`), or — since 2026-09-05, KubeCompat Q1 — a `tvt_` app
    // token on its own, no login step: the client then acts as the token's owner with
    // the owner's permissions, and the backend keeps the token's session for it.
    token?: string;
    validateResponses?: boolean;
    // Injectable fetch — custom CA / dispatcher on Node, defaults to the global fetch.
    fetch?: typeof fetch;
}



export type VirtualMachine = Record<string, unknown>;
export type BatchDeleteResult = { deleted: string[]; refused: { id: string; code: string; detail: string }[] };
export type BatchActionResult = { accepted: string[]; refused: { id: string; code: string; detail: string }[] };
export type Task = Record<string, unknown>;
// `Event` would shadow the DOM/lib type — the prefix keeps call sites honest.
export type TVirtEvent = Record<string, unknown>;
// L'esito della PROVA di una destinazione (#115): `ok` falso non e' un errore
// di trasporto — la prova ha risposto, e la cattiva notizia sta nel problem.
export type AuditRecord = Record<string, unknown>;
export type NotificationTestResult = {
    ok: boolean;
    error?: { code?: string | undefined; title?: string | undefined; detail?: string | undefined } | undefined;
};
export type VmPlacementNode = { node: string; eligible: boolean; reason?: string | undefined; softViolations: { rule: string; detail: string }[] };
export type VmPlacement = { strategy: string; recommended?: string | undefined; nodes: VmPlacementNode[] };
export type VmSpofStructural = { alternatives: number; spof: boolean; bindingConstraint?: string | null | undefined };
// #344 lente di ammissione (ratificata 2026-08-19): la VM può PARTIRE adesso?
// `blockedBy` è parola per parola il 409 che un vm.migrate riceverebbe.
export type VmSpofAdmission = { movableNow: boolean; blockedBy: string | null };
export type VmSpof = { domain: string; asOf: string; node: string | null; alternatives: number; spof: boolean; bindingConstraint?: string | null | undefined; structural?: VmSpofStructural | undefined; admission: VmSpofAdmission; candidates: VmPlacementNode[] };
export type NodeEvacuationReason = { code: string; message: string; node?: string | undefined; rule?: string | undefined };
export type NodeEvacuationItem = { id: string; name: string; kind: string; action: string; target?: string | undefined; reasons: NodeEvacuationReason[] };
export type FailoverReserveShare = { node: string; capacityMib: number };
export type FailoverReserveUnavailable = { node: string; reason: string };
// Per huge-page size (ratified 2026-08-04): the RAM verdict's twin in pages of
// `2M` / `1G` — always both rows, 2M first, zeros when the size has neither
// pools nor huge-page VMs.
export type FailoverReserveHugeSize = {
    size: "2M" | "1G";
    capacityPages: number;
    committedPages: number;
    freePages: number;
    reservePages: number;
    headroomPages: number;
    worstCase: { node: string; capacityPages: number }[];
};
export type FailoverReserveVerdict = {
    state: string;
    tolerateNodeFailures: number;
    providerNodes: number;
    capacityMib: number;
    committedMib: number;
    freeMib: number;
    reserveMib: number;
    headroomMib: number;
    worstCase: FailoverReserveShare[];
    hugePages: FailoverReserveHugeSize[];
    unavailable: FailoverReserveUnavailable[];
};
export type NodeEvacuationPlan = { node: string; strategy: string; items: NodeEvacuationItem[]; failoverReserve?: FailoverReserveVerdict | undefined };
// #233 — the node's read-only service access (T233-P2, R4b): the record on
// the node (`ClusterNode.readOnlyAccess`) and the 202 of the grant, whose
// `password` rides ONLY that response (the joinToken precedent).
export type ReadOnlyAccess = {
    state: "none" | "granted";
    grantedBy?: string | undefined;
    grantedAt?: string | undefined;
    expiresAt?: string | undefined;
    reason?: string | undefined;
    task?: string | undefined;
    appliedAt?: string | undefined;
    endedAt?: string | undefined;
    endedReason?: "revoked" | "expired" | "reboot" | "failed" | undefined;
};
export type ReadOnlyAccessGranted = { task: string; username: string; password: string; expiresAt: string; readOnlyAccess: ReadOnlyAccess };
export type EmergencyAccess = { observedAt: string; lastLoginAt: string | null; lastLoginVia: "ssh" | "console" | "sudo" | "unknown" | null; lastLoginFrom: string | null } | null;
// Round FABRIC F6: the derivation preview/apply vocabulary — the
// NodeEvacuationReason idiom (#133a) with the fabric derivation words (design
// #137; F2 topology, F4 flips). `message` is ready for display, `code` is the
// stable branch key (typed wide on purpose: a code this client does not know
// yet must not take the whole plan down).
export type FabricDerivationState = "manual" | "derived" | "diverged";
export type FabricDerivationReason = { code: string; message: string; network?: string | undefined; device?: string | undefined; address?: string | undefined };
export type FabricNodeConfigChange = { action: "add" | "remove" | "replace"; kind: string; device: string; before?: unknown; after?: unknown };
export type FabricNodeConfigTopology = { form: string; device?: string | undefined; networks: string[]; members?: string[] | undefined; primary?: string | undefined; held?: boolean | undefined; message: string };
// `connections: null` means the observation FAILED — never a negative fact.
export type FabricNodeConfigProbe = { connections?: number | null | undefined; lastActiveAt?: string | null | undefined; emptySince?: string | null | undefined; flows?: number | null | undefined; observedAt?: string | undefined };
export type FabricNodeConfigFlip = { network: string; point: string; address: string; replacedBy: string; phase: "planned" | "draining"; since: string | null; probe: FabricNodeConfigProbe | null; message: string };
export type FabricNodeConfigItem = { node: string; nodeId: string; state: FabricDerivationState; generation: number; previousGeneration: number | null; previousManual: boolean; document?: Record<string, unknown> | null | undefined; diff: FabricNodeConfigChange[]; topology: FabricNodeConfigTopology[]; flips: FabricNodeConfigFlip[]; refusals: FabricDerivationReason[]; warnings: FabricDerivationReason[] };
// Round #405 S2b (§5.3): `fabricEtags` is the COMPOSITE anchor — this fabric,
// the owners of its imported networks, every other fabric the plan's nodes
// are cabled to — sent back WHOLE by the apply.
export type FabricNodeConfigPlan = { fabricId: string; fabricEtags: Record<string, string>; items: FabricNodeConfigItem[] };
// `severActive` is the declared override (the R6 «--force that names the
// loss»): without it a live or unobserved old address refuses speaking.
export type FabricNodeConfigRetire = { node: string; address: string; severActive?: boolean | undefined };
export type FabricNodeConfigApply = { nodes: string[]; fabricEtags: Record<string, string>; expectedGenerations: Record<string, number>; retire?: FabricNodeConfigRetire[] | undefined };
export type FabricNodeConfigOutcome = { node: string; nodeId: string; outcome: "applied" | "refused"; generation?: number | null | undefined; refusals: FabricDerivationReason[]; warnings: FabricDerivationReason[] };
export type FabricNodeConfigApplyResult = { items: FabricNodeConfigOutcome[] };
// RP-P1 (ratified 2026-08-29/31): the H13 restore act (F1-P3) — one node, one
// snapshot, anchored to the generation the operator looked at.
export type FabricNodeConfigRestore = { node: string; expectedGeneration: number };
export type FabricNodeConfigRestoreResult = { node: string; nodeId: string; generation: number };



// The round #102 auth surfaces, hoisted into the facade by round #170 (they used to
// live twice, as private shims of the CLI and of the webui). The types mirror what
// the backend ALWAYS renders: zAuthRealmRendered below tightens the generated schema
// to the render's guarantees, so `id` or `urls` never read as possibly-undefined in
// a client that just listed them.
// #315: the next step, and nothing else — never the realm that resolved the
// identifier (that is the cluster's federation map, and step 1 is unauthenticated).
export type IdentifyResult = { method: "password" | "redirect"; redirectUrl?: string | undefined };
export type SessionCreated = {
    id: string;
    user: { username: string; realm: string; displayName: string };
    createdAt: string;
    expiresAt: string;
    active: boolean;
    mustChangePassword?: boolean | undefined;
    external?: boolean | undefined;
    externalUuid?: string | undefined;
    token: string;
};
export type MfaMethod = { id: string; displayName: string; builtin: boolean; enrollable: boolean; description?: string | undefined };
export type MfaStatus = { required: boolean; requiredVia?: readonly string[] | undefined; enrolled: boolean; methods: string[]; recoveryCodesRemaining?: number | undefined; enrolledAt?: string | undefined };
// One subject's second factor as an addressable resource (round #178 half B):
// managed accounts and directory identities in ONE collection. `orphaned` is
// EXTERNAL-only by ratified asymmetry — a managed enrollment cascades with its user.
export type MfaEnrollment = {
    id: string;
    subject: string;
    realm: string;
    external: boolean;
    user?: string | undefined;
    directoryUuid?: string | undefined;
    methods: readonly string[];
    state: "pending" | "active";
    enrolledAt?: string | undefined;
    recoveryCodesRemaining?: number | undefined;
    lastSeenAt?: string | undefined;
    orphaned: boolean;
    orphanedAt?: string | undefined;
    orphanedReason?: "directory-miss" | "realm-removed" | undefined;
};
export type TotpEnrollment = { secret: string; otpauthUri: string; qrSvg: string; digits: number; periodSeconds: number; algorithm: string };
export type AuthRealm = {
    id: string;
    name: string;
    kind: "ldap";
    enabled: boolean;
    urls: string[];
    startTls: boolean;
    verifyCertificate: boolean;
    caCertificate?: string | undefined;
    bindDn: string;
    bindCredential?: string | undefined;
    baseDn: string;
    userFilter: string;
    memberOfAttribute: string;
    uuidAttribute: string;
    displayNameAttribute: string;
    connectTimeoutSeconds: number;
    notes: string;
    createdAt: string;
    updatedAt: string;
};
export type AuthRealmTestResult = {
    reachable: boolean;
    url?: string | undefined;
    detail?: string | undefined;
    matchedUsers?: number | undefined;
    checks: { name: string; passed: boolean; detail?: string | undefined }[];
};


// The realm render always carries these (renderRealm in the backend): the generated
// schema leaves them optional only because the same shape doubles as the writable
// body. Tightening here keeps the boundary validation AND the ergonomic type.
const zAuthRealmRendered = zAuthRealm.required({
    id: true,
    kind: true,
    urls: true,
    bindDn: true,
    baseDn: true,
    notes: true,
    createdAt: true,
    updatedAt: true,
});



interface TransportResult {
    data?: unknown;
    error?: unknown;
    response: Response;
}



function filterString(term: FilterTerm): string {
    return `${term.field}:${term.op}:${JSON.stringify(term.value)}`;
}



// The ONE place a config PATCH grows its headers (R6 phase 2): the If-Match
// precondition rides along exactly when the caller filed a validator — the
// fabrics.patch form of 2026-08-06, generalized to the whole config surface.
// The ONE place a write grows its `?dryRun=true` (parity R1): absent = the
// write lands, as before.
function writeQuery(opts: WriteOpts): Record<string, boolean> {
    return opts.dryRun === true ? { dryRun: true } : {};
}



function mergePatchHeaders(opts: PatchOpts): Record<string, string> {
    return {
        "content-type": "application/merge-patch+json",
        ...(opts.ifMatch !== undefined ? { "if-match": opts.ifMatch } : {}),
    };
}



// Accepts both the bare `t-…` id and the X-TVirt-Task URL form (#128: one
// collection — the former `mt-` prefix died with the macrotask surface).
function parseTaskRef(ref: string): { id: string } {
    return { id: ref.split("/").pop() ?? ref };
}



// A byte source that KNOWS ITS OWN LENGTH, for content no `Blob` can measure
// (#257, 2026-08-09). `fs.openAsBlob()` on Node 22 reports `.size` MODULO
// 2^32: Luca's 4 526 374 912-byte AlmaLinux ISO measured 231 407 616, and
// `blob.slice()` past that phantom end answered EMPTY — so the CLI declared a
// false `Content-Range` total, shipped every byte it had DECLARED, and the
// server dutifully finalized a truncated file while the CLI printed «upload
// complete» and exited 0. Silent data loss with a green verdict.
// A caller that can measure itself (the CLI stats the very filehandle it then
// reads from) hands one of these instead of a Blob: `sizeBytes` is the truth
// and `readChunk` fetches [start, end) on demand, so nothing but the chunk in
// flight ever lands in memory.
export interface UploadSource {
    sizeBytes: number;
    // [start, end) — MUST answer exactly `end - start` bytes. A short answer
    // means the declared size was a lie (or the file shrank underfoot) and the
    // uploader refuses the upload rather than finalize a hole.
    readChunk(start: number, end: number): Promise<Uint8Array>;
}



// Blob and Uint8Array are sources too — one code path, one set of guards.
// (A Blob's `.size` is taken on faith: only its producer can know better,
// which is exactly why the CLI stopped producing one.)
function uploadSourceOf(content: Uint8Array | Blob | UploadSource): UploadSource {
    if (content instanceof Blob) {
        return {
            sizeBytes: content.size,
            readChunk: async (start: number, end: number): Promise<Uint8Array> =>
                new Uint8Array(await content.slice(start, end).arrayBuffer()),
        };
    }
    if (content instanceof Uint8Array) {
        return {
            sizeBytes: content.byteLength,
            readChunk: (start: number, end: number): Promise<Uint8Array> =>
                Promise.resolve(content.subarray(start, end)),
        };
    }
    return content;
}



// The structural half of the "never a false complete" guard (#257): every
// chunk that goes on the wire passes through here, so a source that cannot
// deliver what it promised stops the upload at the FIRST short read instead
// of at the finalize that would have blessed the truncation.
async function readUploadChunk(source: UploadSource, start: number, end: number): Promise<Uint8Array> {
    const chunk = await source.readChunk(start, end);
    if (chunk.byteLength !== end - start) {
        throw new TVirtApiError(500, {
            title: "Upload source truncated",
            detail: `the source answered ${chunk.byteLength} bytes for [${start}, ${end}) of a declared ${source.sizeBytes} — refusing to finalize a partial upload`,
        });
    }
    return chunk;
}



// eslint-disable-next-line @typescript-eslint/explicit-function-return-type -- the facade's shape IS its inferred type
export function createClientV6(options: ClientOptionsV6) {
    let token: string | null = options.token ?? null;
    let sudoToken: string | null = null;
    const validate = options.validateResponses ?? true;
    const fetchImpl = options.fetch ?? fetch;
    const transport = createFetchClient<paths>({ baseUrl: options.baseUrl, fetch: fetchImpl });

    transport.use({
        onRequest({ request }) {
            if (token !== null) {
                request.headers.set("authorization", `Bearer ${token}`);
            }
            if (sudoToken !== null) {
                request.headers.set("x-tvirt-sudo", sudoToken);
            }
            return request;
        },
    });



    function unwrap<T>(result: TransportResult, schema?: z.ZodType<T>): T {
        if (result.error !== undefined) {
            throw new TVirtApiError(result.response.status, result.error as Record<string, unknown>);
        }
        if (validate && schema !== undefined) {
            const parsed = schema.safeParse(result.data);
            if (!parsed.success) {
                throw new TVirtResponseError(result.response.url, parsed.error, result.data);
            }
            return parsed.data;
        }
        return result.data as T;
    }



    function listQuery(p: ListParams): Record<string, unknown> {
        const query: Record<string, unknown> = {};
        if (p.filter !== undefined && p.filter.length > 0) {
            query["filter"] = p.filter.map(filterString);
        }
        if (p.sort !== undefined) {
            query["sort"] = `${p.sort.direction === "desc" ? "-" : ""}${p.sort.field}`;
        }
        if (p.scope !== undefined) {
            query["scope"] = p.scope;
        }
        if (p.limit !== undefined) {
            query["limit"] = p.limit;
        }
        if (p.cursor !== undefined && p.cursor !== null) {
            query["cursor"] = p.cursor;
        }
        return query;
    }



    // One unreadable item does not condemn the page: it is set aside in
    // `unreadable` (round «errori onesti») instead of rejecting the call, so a
    // list stays usable when a single object carries a value this client does
    // not know — and the caller still has the field and the value to say so.
    function page<T>(result: TransportResult, schema: z.ZodType<T>): Page<T> {
        const body = unwrap<{ items: unknown[]; nextCursor?: string; stale?: boolean; stateAsOf?: string | null }>(result);
        const envelope = {
            nextCursor: body.nextCursor ?? null,
            ...(body.stale === true ? { stale: true } : {}),
            ...(body.stale === true && (typeof body.stateAsOf === "string" || body.stateAsOf === null) ? { stateAsOf: body.stateAsOf } : {}),
        };
        if (!validate) {
            return { items: body.items as T[], ...envelope };
        }
        const items: T[] = [];
        const unreadable: TVirtResponseError[] = [];
        body.items.forEach((item, index) => {
            const parsed = schema.safeParse(item);
            if (parsed.success) {
                items.push(parsed.data);
            }
            else {
                unreadable.push(new TVirtResponseError(result.response.url, parsed.error, item, `items.${index}`));
            }
        });
        return { items, ...envelope, ...(unreadable.length > 0 ? { unreadable } : {}) };
    }



    function writeResult<T>(result: TransportResult, schema?: z.ZodType<T>): WriteResult<T> {
        const resource = unwrap<T>(result, schema);
        return { resource, task: result.response.headers.get("x-tvirt-task") };
    }



    const tasks = {
        list: async (p: ListParams<FilterableFields["listTasks"]> = {}): Promise<Page<Task>> =>
            page(await transport.GET("/api/v6/tasks", { params: { query: listQuery(p) as never } }), zTask),

        listAll: (p: Omit<ListParams<FilterableFields["listTasks"]>, "cursor"> = {}): PageIterator<Task> =>
            genPageIterator(async ({ cursor }) => tasks.list({ ...p, cursor })),

        get: async (ref: string): Promise<Task> => {
            const { id } = parseTaskRef(ref);
            return unwrap(await transport.GET("/api/v6/tasks/{task}", { params: { path: { task: id } } }), zTask);
        },

        // The children of a parent task (#128) — resume history (`superseded`)
        // included; filter it out for the CURRENT attempts. Sorted by id
        // (uuidv7 ⇒ birth order: the original steps in sequence, resume
        // attempts after); `stepSeq` on each row is the authoritative order.
        children: async (ref: string, p: Omit<ListParams<FilterableFields["listTasks"]>, "filter"> = {}): Promise<Page<Task>> => {
            const { id } = parseTaskRef(ref);
            return tasks.list({ ...p, filter: [{ field: "parent", op: "eq", value: `t-${id.replace(/^t-/, "")}` }], sort: { field: "id", direction: "asc" } });
        },

        // Cooperative cancellation (§9): leaves at safe points, parents at step
        // boundaries — one operation since #128.
        cancel: async (ref: string): Promise<void> => {
            const { id } = parseTaskRef(ref);
            unwrap(await transport.POST("/api/v6/tasks/{task}/actions/cancel", { params: { path: { task: id } } }));
        },

        // Halted-parent management (§5.7, #128): resume retries from the failed
        // step, abort runs the declared undo actions of completed steps in reverse.
        resume: async (ref: string): Promise<Record<string, unknown>> => {
            const { id } = parseTaskRef(ref);
            return unwrap(await transport.POST("/api/v6/tasks/{task}/actions/resume", { params: { path: { task: id } } }));
        },

        abort: async (ref: string): Promise<Record<string, unknown>> => {
            const { id } = parseTaskRef(ref);
            return unwrap(await transport.POST("/api/v6/tasks/{task}/actions/abort", { params: { path: { task: id } } }));
        },

        // Polls until the task reaches a terminal phase; resolves with the final
        // render on success, throws TVirtTaskError otherwise. A spent wait
        // budget is NOT a verdict: it throws TVirtWaitTimeoutError so the
        // caller can keep waiting or say "outcome unknown" — a task the
        // engine still holds may yet succeed (and does, e.g. behind a worker
        // restart), so mapping this onto "failed" lies to the user.
        wait: async (ref: string, opts: { pollMs?: number; timeoutMs?: number; onProgress?: (task: Task) => void } = {}): Promise<Task> => {
            const deadline = Date.now() + (opts.timeoutMs ?? 120_000);
            let last: Task | undefined;
            for (;;) {
                const task = await tasks.get(ref);
                last = task;
                opts.onProgress?.(task);
                const phase = String(task["phase"]);
                if (phase === "succeeded") {
                    return task;
                }
                if (phase === "failed" || phase === "cancelled" || phase === "aborted") {
                    throw new TVirtTaskError(task);
                }
                if (Date.now() > deadline) {
                    throw new TVirtWaitTimeoutError(ref, last);
                }
                await new Promise((r) => setTimeout(r, opts.pollMs ?? 250));
            }
        },

        follow: async function* (ref: string): AsyncGenerator<Record<string, unknown>> {
            const { id } = parseTaskRef(ref);
            const headers: Record<string, string> = {};
            if (token !== null) {
                headers["authorization"] = `Bearer ${token}`;
            }
            const response = await fetchImpl(`${options.baseUrl}/api/v6/tasks/${id}/log?follow=true`, { headers });
            if (!response.ok || response.body === null) {
                throw new TVirtApiError(response.status, (await response.json().catch(() => ({}))) as Record<string, unknown>);
            }
            yield* ndjsonLines(response.body);
        },

        // The structured log as a plain array (frames so far; the whole log once the
        // task is terminal): the non-follow read of /tasks/{id}/log.
        log: async (ref: string): Promise<Record<string, unknown>[]> => {
            const frames: Record<string, unknown>[] = [];
            const { id } = parseTaskRef(ref);
            const headers: Record<string, string> = {};
            if (token !== null) {
                headers["authorization"] = `Bearer ${token}`;
            }
            const response = await fetchImpl(`${options.baseUrl}/api/v6/tasks/${id}/log`, { headers });
            if (!response.ok || response.body === null) {
                throw new TVirtApiError(response.status, (await response.json().catch(() => ({}))) as Record<string, unknown>);
            }
            for await (const frame of ndjsonLines(response.body)) {
                frames.push(frame);
            }
            return frames;
        },
    };



    // The cluster event journal (task #226 — read-only by construction; the
    // HA family rides it since the A11 fold, 2026-08-09): newest-first with
    // the standard filter grammar — a resource's history is
    // `filter: [{ field: "subject.id", op: "eq", value: id }]`, the HA lens is
    // `type: in` over the `ha.*` vocabulary. 503 while the store is down.
    const events = {
        list: async (p: ListParams<FilterableFields["listEvents"]> = {}): Promise<Page<TVirtEvent>> =>
            page(await transport.GET("/api/v6/events", { params: { query: listQuery(p) as never } }), zEvent),

        listAll: (p: Omit<ListParams<FilterableFields["listEvents"]>, "cursor"> = {}): PageIterator<TVirtEvent> =>
            genPageIterator(async ({ cursor }) => events.list({ ...p, cursor })),

        get: async (id: string): Promise<TVirtEvent> =>
            unwrap(await transport.GET("/api/v6/events/{event}", { params: { path: { event: id } } }), zEvent),
    };




    // Il registro d'audit (#113): il FRATELLO del giornale, non la sua vista —
    // qui vivono gli atti TENTATI con chi li ha chiesti, i rifiuti compresi
    // (un 403 non e' un fatto compiuto e nel giornale non esiste). Sola
    // lettura per costruzione: due GET, nessuna scrittura, nessuna
    // cancellazione. 503 a store giu', come il giornale.
    const audit = {
        list: async (p: ListParams<FilterableFields["listAuditRecords"]> = {}): Promise<Page<AuditRecord>> =>
            page(await transport.GET("/api/v6/audit", { params: { query: listQuery(p) as never } }), zAuditRecord),

        listAll: (p: Omit<ListParams<FilterableFields["listAuditRecords"]>, "cursor"> = {}): PageIterator<AuditRecord> =>
            genPageIterator(async ({ cursor }) => audit.list({ ...p, cursor })),

        get: async (id: string): Promise<AuditRecord> =>
            unwrap(await transport.GET("/api/v6/audit/{record}", { params: { path: { record: id } } }), zAuditRecord),
    };

    // Le notifiche (task #115): il consumatore del giornale. Tre collezioni e
    // due azioni — la prova di una destinazione, che risponde 200 anche
    // fallendo (la cattiva notizia sta nel corpo, col problem che la spiega), e
    // la rimessa in coda di una consegna fallita. 503 a store giu', come il
    // giornale che consumano.
    const notificationTargets = {
        list: async (p: ListParams<FilterableFields["listNotificationTargets"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/notificationTargets", { params: { query: listQuery(p) as never } }), zNotificationTarget),

        listAll: (p: Omit<ListParams<FilterableFields["listNotificationTargets"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => notificationTargets.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/notificationTargets/{target}", { params: { path: { target: id } } }), zNotificationTarget),

        create: async (body: Record<string, unknown>): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/notificationTargets", { body: body as never }), zNotificationTarget),

        patch: async (id: string, mergePatch: Record<string, unknown>): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/notificationTargets/{target}", {
                params: { path: { target: id } },
                body: mergePatch as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zNotificationTarget),

        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v6/notificationTargets/{target}", { params: { path: { target: id } } }));
        },

        test: async (id: string): Promise<NotificationTestResult> =>
            unwrap(await transport.POST("/api/v6/notificationTargets/{target}/actions/test", {
                params: { path: { target: id } },
            }), zNotificationTestResult),
    };



    const notificationRules = {
        list: async (p: ListParams<FilterableFields["listNotificationRules"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/notificationRules", { params: { query: listQuery(p) as never } }), zNotificationRule),

        listAll: (p: Omit<ListParams<FilterableFields["listNotificationRules"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => notificationRules.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/notificationRules/{rule}", { params: { path: { rule: id } } }), zNotificationRule),

        create: async (body: Record<string, unknown>): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/notificationRules", { body: body as never }), zNotificationRule),

        patch: async (id: string, mergePatch: Record<string, unknown>): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/notificationRules/{rule}", {
                params: { path: { rule: id } },
                body: mergePatch as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zNotificationRule),

        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v6/notificationRules/{rule}", { params: { path: { rule: id } } }));
        },
    };



    const notificationDeliveries = {
        list: async (p: ListParams<FilterableFields["listNotificationDeliveries"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/notificationDeliveries", { params: { query: listQuery(p) as never } }), zNotificationDelivery),

        listAll: (p: Omit<ListParams<FilterableFields["listNotificationDeliveries"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => notificationDeliveries.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/notificationDeliveries/{delivery}", { params: { path: { delivery: id } } }), zNotificationDelivery),

        retry: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/notificationDeliveries/{delivery}/actions/retry", {
                params: { path: { delivery: id } },
            }), zNotificationDelivery),
    };



    const vms = {
        list: async (p: ListParams<FilterableFields["listVms"]> = {}): Promise<Page<VirtualMachine>> =>
            page(await transport.GET("/api/v6/vms", { params: { query: listQuery(p) as never } }), zVirtualMachine),

        listAll: (p: Omit<ListParams<FilterableFields["listVms"]>, "cursor"> = {}): PageIterator<VirtualMachine> =>
            genPageIterator(async ({ cursor }) => vms.list({ ...p, cursor })),

        get: async (id: string): Promise<VirtualMachine> =>
            unwrap(await transport.GET("/api/v6/vms/{vm}", { params: { path: { vm: id } } }), zVirtualMachine),

        create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<WriteResult<VirtualMachine>> =>
            writeResult(await transport.POST("/api/v6/vms", { params: { query: writeQuery(opts) as never }, body: body as never }), zVirtualMachine),

        // ?live (ratified 2026-07-12): apply cdroms[].image / nics[].connected over
        // QMP immediately — `task` carries the vm.applyDevices reference when used.
        patch: async (id: string, mergePatch: Record<string, unknown>, opts: { live?: boolean } & PatchOpts = {}): Promise<WriteResult<VirtualMachine>> =>
            writeResult(await transport.PATCH("/api/v6/vms/{vm}", {
                params: { path: { vm: id }, query: { ...(opts.live === true ? { live: true } : {}), ...writeQuery(opts) } as never },
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zVirtualMachine),

        delete: async (id: string, opts: { keepDisks?: boolean } = {}): Promise<Task> =>
            unwrap(await transport.DELETE("/api/v6/vms/{vm}", {
                params: { path: { vm: id }, query: (opts.keepDisks === true ? { keepDisks: true } : {}) as never },
            }), zTask),

        // Explicit `node` (task #33): an operator preference among the valid
        // candidates, else Proximity auto-places. An unfit node answers a
        // speaking 422 (unwrap surfaces it as a TVirtApiError).
        //
        // `forceCordoned` (#373): the explicit consent that supersedes the §8.1
        // maintenance cordon on the EMERGENCY lane (store down) — without it a
        // cordoned, or unreadable, cordon answers `409 cordon_raised`. Inert
        // with the store up.
        start: async (id: string, opts: { node?: NodeRef; forceCordoned?: boolean } = {}): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/vms/{vm}/actions/start", {
                params: { path: { vm: id } },
                body: {
                    ...(opts.node !== undefined ? { node: opts.node } : {}),
                    ...(opts.forceCordoned === true ? { forceCordoned: true } : {}),
                } as never,
            }), zTask),

        // The start placement census (task #33): which members may host the VM,
        // why the rest cannot, and the auto-placement recommendation — feeds the
        // destination select in the start dialog.
        placement: async (id: string): Promise<VmPlacement> =>
            unwrap(await transport.GET("/api/v6/vms/{vm}/placement", { params: { path: { vm: id } } }), zVmPlacement),

        // #344 (get-spof, first cut): the VM's independence report — how many
        // OTHER members could host it right now, answered by the placement
        // engine itself, with THE binding constraint named.
        spof: async (id: string): Promise<VmSpof> =>
            unwrap(await transport.GET("/api/v6/vms/{vm}/spof", { params: { path: { vm: id } } }), zVmSpof),

        // §8.2 live migration (ratified 2026-07-24): 202 + the vm.migrate
        // parent-task ref; node absent ⇒ Proximity picks the target.
        migrate: async (id: string, opts: { node?: NodeRef } = {}): Promise<WriteResult<Record<string, unknown>>> =>
            writeResult(await transport.POST("/api/v6/vms/{vm}/actions/migrate", {
                params: { path: { vm: id } },
                body: (opts.node !== undefined ? { node: opts.node } : {}) as never,
            })),

        // Cold storage move (storage-move round 2026-07-28, ratifica #31): every
        // attached disk onto the target storage — 202 + the vm.moveStorage
        // parent task (one disk.move step per disk, disks already home skipped).
        moveStorage: async (id: string, body: { storage: string }): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/vms/{vm}/actions/moveStorage", { params: { path: { vm: id } }, body: body as never }), zTask),

        // Scope move (scope-move round, ratified 2026-08-01): the dedicated
        // move operation — synchronous config write, the refreshed VM comes
        // back with the new scope; owned disks follow silently. dryRun runs
        // the full admission without persisting.
        moveScope: async (id: string, body: { scope: string }, opts: { dryRun?: boolean } = {}): Promise<VirtualMachine> =>
            unwrap(await transport.POST("/api/v6/vms/{vm}/actions/moveScope", {
                params: { path: { vm: id }, query: (opts.dryRun === true ? { dryRun: true } : {}) as never },
                body: body as never,
            }), zVirtualMachine),

        stop: async (id: string, body: { mode?: "acpi" | "hard"; timeoutSeconds?: number } = {}): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/vms/{vm}/actions/stop", { params: { path: { vm: id } }, body: body as never }), zTask),

        reset: async (id: string): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/vms/{vm}/actions/reset", { params: { path: { vm: id } } }), zTask),

        pause: async (id: string): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/vms/{vm}/actions/pause", { params: { path: { vm: id } } }), zTask),

        // `forceCordoned` (#373): see `start` — `vm.resume` sits in exactly the
        // same place, outside the §8.1 allow-list the emergency claim narrows to.
        resume: async (id: string, opts: { forceCordoned?: boolean } = {}): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/vms/{vm}/actions/resume", {
                params: { path: { vm: id } },
                body: (opts.forceCordoned === true ? { forceCordoned: true } : {}) as never,
            }), zTask),

        batchDelete: async (ids: string[], opts: { keepDisks?: boolean } = {}): Promise<BatchDeleteResult> =>
            unwrap(await transport.DELETE("/api/v6/vms/batch", {
                body: { ids, ...(opts.keepDisks === true ? { keepDisks: true } : {}) } as never,
            }), zBatchDeleteResult),

        batchStart: async (ids: string[]): Promise<BatchActionResult> =>
            unwrap(await transport.POST("/api/v6/vms/batch/actions/start", { body: { ids } as never }), zBatchActionResult),

        batchStop: async (ids: string[], body: { mode?: "acpi" | "hard"; timeoutSeconds?: number } = {}): Promise<BatchActionResult> =>
            unwrap(await transport.POST("/api/v6/vms/batch/actions/stop", { body: { ids, ...body } as never }), zBatchActionResult),

        batchPause: async (ids: string[]): Promise<BatchActionResult> =>
            unwrap(await transport.POST("/api/v6/vms/batch/actions/pause", { body: { ids } as never }), zBatchActionResult),

        batchResume: async (ids: string[]): Promise<BatchActionResult> =>
            unwrap(await transport.POST("/api/v6/vms/batch/actions/resume", { body: { ids } as never }), zBatchActionResult),

        batchReset: async (ids: string[]): Promise<BatchActionResult> =>
            unwrap(await transport.POST("/api/v6/vms/batch/actions/reset", { body: { ids } as never }), zBatchActionResult),

        // Authority transfer (cluster-first §8, Fase 5): synchronous config — the
        // refreshed VM render comes back, no task. Requires sudo.
        transferAuthority: async (id: string, node: string): Promise<VirtualMachine> =>
            unwrap(await transport.POST("/api/v6/vms/{vm}/actions/transferAuthority", { params: { path: { vm: id } }, body: { node } as never }), zVirtualMachine),

        // TVirt Guest Tools (round #110): ADDS a dedicated CD drive holding the
        // node-local Guest Tools ISO (ejecting removes the drive) — 200 with the
        // refreshed VM; on a running/paused VM the drive is hotplugged and
        // `task` carries the vm.applyDevices reference.
        mountGuestTools: async (id: string): Promise<WriteResult<VirtualMachine>> =>
            writeResult(await transport.POST("/api/v6/vms/{vm}/actions/mountGuestTools", {
                params: { path: { vm: id } },
            }), zVirtualMachine),

        // Round D (ratifica #10a, 2026-07-28): create+attach in ONE call — a
        // blank OWNED disk born in the VM's scope and attached at `index`
        // (default: lowest free slot). 201 with the new Disk; `task` carries
        // its disk.create allocation reference.
        createDisk: async (id: string, body: { storage: string; sizeGiB: number; index?: number; name?: string; format?: "qcow2" | "raw"; encrypted?: boolean }): Promise<WriteResult<Record<string, unknown>>> =>
            writeResult(await transport.POST("/api/v6/vms/{vm}/actions/createDisk", { params: { path: { vm: id } }, body: body as never }), zDisk),

        // Round D (ratifica #10b, 2026-07-28): the first-class detach — 200
        // with the refreshed VM; with `delete: true` the detached disk is also
        // destroyed and the answer is 202 with the disk.delete Task.
        detachDisk: async (id: string, body: { index: number; delete?: boolean }): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/vms/{vm}/actions/detachDisk", { params: { path: { vm: id } }, body: body as never })),

        snapshot: async (id: string, body: { name: string; memory?: boolean; requireQuiesce?: boolean }): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/vms/{vm}/actions/snapshot", { params: { path: { vm: id } }, body: body as never }), zTask),

        revert: async (id: string, snapshot: string): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/vms/{vm}/actions/revert", { params: { path: { vm: id } }, body: { snapshot } as never }), zTask),

        // #279 (2026-09-03): a node with descendants MERGES forward into its
        // readers; `acceptDuplication` is the consent the 409 asks for when
        // the readers are several (the delta is copied into each of them).
        deleteSnapshot: async (id: string, name: string, opts: { acceptDuplication?: boolean } = {}): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/vms/{vm}/actions/deleteSnapshot", {
                params: { path: { vm: id } },
                body: { name, ...(opts.acceptDuplication === true ? { acceptDuplication: true } : {}) } as never,
            }), zTask),

        // `markCloneAs` (round Template, ratified 2026-07-28): null/absent = the
        // clone inherits the source's kind; "vm" = the one-step deploy from a
        // template; "template" = the one-step promotion.
        // `preferredNode` (Round F): absent = the clone inherits the source's
        // start preference; a member name sets it; null clears it.
        clone: async (id: string, body: { name: string; scope?: string; storage?: string; linked?: boolean; markCloneAs?: "vm" | "template" | null; preferredNode?: string | null }): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/vms/{vm}/actions/clone", { params: { path: { vm: id } }, body: body as never }), zTask),

        // TVirt QuickDeploy (round #110, T110-P2): deploy from a template that
        // declares `quickDeploy.os` — 202 + ONE parent task targeting the
        // first newborn; `params.instances` lists them all. The structured
        // grammar (T110-P3) is the body; `userData`/`networkConfig` raw are
        // the explicit fallback that supersedes the generators.
        quickDeploy: async (id: string, body: {
            name: string;
            scope?: string;
            storage?: string;
            linked?: boolean;
            start?: boolean;
            count?: number;
            os?: "linux" | "windows";
            hostname?: string;
            ip4?: string;
            gw4?: string;
            dns?: string[];
            linux?: { user?: string; password?: string; sshKeys?: string[] };
            windows?: { user?: string; password?: string; productKey?: string; kmsServer?: string; joinAd?: { domain: string; user: string; password: string; ou?: string } };
            userData?: string | null;
            networkConfig?: string | null;
            secretFields?: string[];
        }): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/vms/{vm}/actions/quickDeploy", { params: { path: { vm: id } }, body: body as never }), zTask),

        // Template transitions (round Template): synchronous config — the
        // refreshed VM comes back, no task. Mark requires the VM stopped with
        // no active task; unmark has no special requirement.
        markTemplate: async (id: string): Promise<VirtualMachine> =>
            unwrap(await transport.POST("/api/v6/vms/{vm}/actions/markTemplate", { params: { path: { vm: id } } }), zVirtualMachine),

        unmarkTemplate: async (id: string): Promise<VirtualMachine> =>
            unwrap(await transport.POST("/api/v6/vms/{vm}/actions/unmarkTemplate", { params: { path: { vm: id } } }), zVirtualMachine),

        // Round K3 (#64): pack the TEMPLATE into a .tvtpl catalog file —
        // 202 + Task, the new catalog file is the task's target.
        exportToCatalog: async (id: string, body: { catalog: string; name?: string; storage?: string }): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/vms/{vm}/actions/exportToCatalog", { params: { path: { vm: id } }, body: body as never }), zTask),

        // Issues a fresh viewer ticket (superseding every previous one for the VM)
        // and returns the raw-VNC WebSocket URL (POST — a ticket is created, 2026-07-10).
        // Historical series (Annex I): worker-sampled, bucketed server-side.
        // #111: the window may be relative (`range`) or absolute (`from`/`to`),
        // `agg` picks weighted mean vs per-bucket peak, and the answer declares
        // the TIER it came from and WHERE it was served from.
        stats: async (id: string, opts: { metric: string[]; range?: string; from?: string; to?: string; step?: number; agg?: "avg" | "max" }): Promise<{ step: number; resolution: number | null; servedFrom: "store" | "node"; series: Record<string, unknown>[] }> =>
            unwrap(await transport.GET("/api/v6/vms/{vm}/stats", {
                params: {
                    path: { vm: id },
                    query: {
                        metric: opts.metric.join(","),
                        ...(opts.range !== undefined ? { range: opts.range } : {}),
                        ...(opts.from !== undefined ? { from: opts.from } : {}),
                        ...(opts.to !== undefined ? { to: opts.to } : {}),
                        ...(opts.step !== undefined ? { step: opts.step } : {}),
                        ...(opts.agg !== undefined ? { agg: opts.agg } : {}),
                    } as never,
                },
            }), zStatsSeries),

        // The latest display capture, refreshed by the worker's supervision pass.
        screenshot: async (id: string): Promise<{ data: Uint8Array; capturedAt: string | null }> => {
            const response = await fetchImpl(`${options.baseUrl}/api/v6/vms/${id}/screenshot`, {
                headers: authHeaders(),
            });
            if (!response.ok) {
                throw new TVirtApiError(response.status, (await response.json().catch(() => ({}))) as Record<string, unknown>);
            }
            return {
                data: new Uint8Array(await response.arrayBuffer()),
                capturedAt: response.headers.get("x-tvirt-captured-at"),
            };
        },

        view: async (id: string): Promise<{ viewerWebSocketUrl: string; proto: string }> =>
            unwrap(await transport.POST("/api/v6/vms/{vm}/actions/view", {
                params: { path: { vm: id }, query: { proto: "vnc" } as never },
            }), zViewerAccess),

        // The fate of a viewer ticket (#32, in-contract since #148): how a
        // dead WebSocket tells a takeover from a blip. Ticket-authenticated —
        // the tvvw_ ticket is the credential, always 200 (unknown on a miss).
        viewerFate: async (id: string, ticket: string): Promise<z.infer<typeof zViewerFate>> =>
            unwrap(await transport.GET("/api/v6/vms/{vm}/viewer/{ticket}/fate", {
                params: { path: { vm: id, ticket } },
            }), zViewerFate),
    };



    const storages = {
        list: async (p: ListParams<FilterableFields["listStorage"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/storages", { params: { query: listQuery(p) as never } }), zStorage),

        listAll: (p: Omit<ListParams<FilterableFields["listStorage"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => storages.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/storages/{storage}", { params: { path: { storage: id } } }), zStorage),

        create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<WriteResult<Record<string, unknown>>> =>
            writeResult(await transport.POST("/api/v6/storages", { params: { query: writeQuery(opts) as never }, body: body as never }), zStorage),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/storages/{storage}", {
                params: { path: { storage: id }, query: writeQuery(opts) as never },
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zStorage),

        // #355 — `releaseImages` confirms deleting a pool that HOLDS
        // materialized OCI images (`release_required` otherwise). It destroys
        // nothing: the delete unregisters, so the blobs stay on the namespace
        // and every scope's reference survives as a claim — the images simply
        // stop being materialized until the pool is registered again.
        delete: async (id: string, opts: { releaseImages?: boolean } = {}): Promise<void> => {
            unwrap(await transport.DELETE("/api/v6/storages/{storage}", {
                params: { path: { storage: id }, query: (opts.releaseImages === true ? { releaseImages: true } : {}) as never },
            }));
        },

        batchDelete: async (ids: string[], opts: { releaseImages?: boolean } = {}): Promise<BatchDeleteResult> =>
            unwrap(await transport.DELETE("/api/v6/storages/batch", {
                params: { query: (opts.releaseImages === true ? { releaseImages: true } : {}) as never },
                body: { ids } as never,
            }), zBatchDeleteResult),

        // Operator attestation (mirrors nodes.attestDown, 2026-07-23): the backing is
        // declared irrecoverable — tasks fail, deletes go record-only, bytes untouched. Sudo.
        initialize: async (id: string): Promise<Record<string, unknown>> => {
            const res = await transport.POST("/api/v6/storages/{storage}/actions/initialize", {
                params: { path: { storage: id } },
            });
            return unwrap<Record<string, unknown>>(res);
        },

        attestLost: async (id: string): Promise<void> => {
            unwrap(await transport.POST("/api/v6/storages/{storage}/actions/attestLost", { params: { path: { storage: id } } }));
        },

        // Round R4: the orphan volume group an operator decided to adopt, after
        // reading the `volumeGroup` finding that reports it. Both facts the
        // finding shows travel with the request — the DEVICE is the identity,
        // the name is how a human recognized it — and the worker re-probes both
        // before renaming anything. 202 + Task. Sudo.
        adoptVolumeGroup: async (id: string, device: string, volumeGroup: string): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/storages/{storage}/actions/adopt", {
                params: { path: { storage: id } },
                body: { device, volumeGroup } as never,
            }), zTask),

        // The scan walk (Annex G.4): re-inventories the namespace and refreshes
        // the findings — 202 + Task.
        scan: async (id: string): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/storages/{storage}/actions/scan", { params: { path: { storage: id } } }), zTask),

        // The configured census (round K1): which objects LIVE on the storage,
        // sorted by kind then name — the "what would I lose" view.
        objects: async (id: string): Promise<{ items: Record<string, unknown>[] }> =>
            unwrap(await transport.GET("/api/v6/storages/{storage}/objects", { params: { path: { storage: id } } }), zListStorageObjectsResponse),

        findings: async (id: string): Promise<{ items: Record<string, unknown>[] }> =>
            unwrap(await transport.GET("/api/v6/storages/{storage}/findings", { params: { path: { storage: id } } }), zListStorageFindingsResponse),

        // Decide a finding: 200 with the finding (adopt/dismiss) or 202 with the
        // purge Task (and adopt of kind namespace) — the caller branches on the
        // shape. Adopting a catalogFile finding REQUIRES the target catalog
        // reference (round K1) — ignored otherwise.
        decideFinding: async (id: string, findingId: string, action: "adopt" | "purge" | "dismiss", opts: { catalog?: string } = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/storages/{storage}/findings/{finding}", {
                params: { path: { storage: id, finding: findingId } },
                body: { action, ...(opts.catalog !== undefined ? { catalog: opts.catalog } : {}) } as never,
            }), zActOnStorageFindingResponse),
    };



    // The cluster keystore (ratified 2026-07-23): pure config, replicated — no
    // task ever rides a write. The secret is WRITE-ONLY: required at creation,
    // never rendered back; a PATCH carrying `secret` is the rotation.
    const credentials = {
        list: async (p: ListParams<FilterableFields["listCredentials"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/credentials", { params: { query: listQuery(p) as never } }), zCredential),

        listAll: (p: Omit<ListParams<FilterableFields["listCredentials"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => credentials.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/credentials/{credential}", { params: { path: { credential: id } } }), zCredential),

        create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/credentials", { params: { query: writeQuery(opts) as never }, body: body as never }), zCredential),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/credentials/{credential}", {
                params: { path: { credential: id }, query: writeQuery(opts) as never },
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zCredential),

        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v6/credentials/{credential}", { params: { path: { credential: id } } }));
        },

        // PKI (round #159): CAs and certificates as first-class kinds. The
        // private key never rides a response — the readable side is the
        // derived `x509` block, the PEM export and the per-CA CRL.
        generateCa: async (body: Record<string, unknown>): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/credentials/actions/generateCa", { body: body as never }), zCredential),

        issueCertificate: async (id: string, body: Record<string, unknown>): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/credentials/{credential}/actions/issueCertificate", {
                params: { path: { credential: id } },
                body: body as never,
            }), zCredential),

        revokeCertificate: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/credentials/{credential}/actions/revoke", { params: { path: { credential: id } } }), zCredential),

        exportPem: async (id: string, opts: { chain?: boolean } = {}): Promise<{ pem: string }> =>
            unwrap(await transport.GET("/api/v6/credentials/{credential}/pem", {
                params: { path: { credential: id }, query: opts.chain === true ? { chain: true } : {} },
            }), zExportCredentialPemResponse),

        crl: async (id: string): Promise<{ pem: string }> =>
            unwrap(await transport.GET("/api/v6/credentials/{credential}/crl", { params: { path: { credential: id } } }), zGetCredentialCrlResponse),
    };



    // OCI wave 1 (Annex Q, 2026-07-27 — the tag-as-branch model): the registry
    // anagraphics. Pure config CRUD; `verify` is the honest probe — a 202 Task
    // whose worker runs a skopeo login/list attempt and stamps the observed
    // verify group on the registry.
    const ociRegistries = {
        list: async (p: ListParams<FilterableFields["listOciRegistries"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/oci/registries", { params: { query: listQuery(p) as never } }), zOciRegistry),

        listAll: (p: Omit<ListParams<FilterableFields["listOciRegistries"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => ociRegistries.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/oci/registries/{registry}", { params: { path: { registry: id } } }), zOciRegistry),

        create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/oci/registries", { params: { query: writeQuery(opts) as never }, body: body as never }), zOciRegistry),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/oci/registries/{registry}", {
                params: { path: { registry: id }, query: writeQuery(opts) as never },
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zOciRegistry),

        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v6/oci/registries/{registry}", { params: { path: { registry: id } } }));
        },

        batchDelete: async (ids: string[]): Promise<BatchDeleteResult> =>
            unwrap(await transport.DELETE("/api/v6/oci/registries/batch", { body: { ids } as never }), zBatchDeleteResult),

        verify: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/oci/registries/{registry}/actions/verify", { params: { path: { registry: id } } })),

        // The explore surface (Annex Q wave 2, paginated round #90): all are
        // lease-free 202 tasks whose STRUCTURED answer rides the task
        // `result` — poll with tasks.wait and read `result` (the ceph.query
        // pattern). search/listRemoteTags answer `nextCursor` when more
        // exists: pass it back verbatim for the next page; listRemoteTags
        // also takes a server-side `term` filter.
        search: async (id: string, term: string, page: { cursor?: string; pageSize?: number } = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/oci/registries/{registry}/actions/search", {
                params: { path: { registry: id } },
                body: {
                    term,
                    ...(page.cursor !== undefined ? { cursor: page.cursor } : {}),
                    ...(page.pageSize !== undefined ? { pageSize: page.pageSize } : {}),
                } as never,
            })),

        listRemoteTags: async (id: string, path: string, page: { term?: string; cursor?: string; pageSize?: number } = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/oci/registries/{registry}/actions/listRemoteTags", {
                params: { path: { registry: id } },
                body: {
                    path,
                    ...(page.term !== undefined ? { term: page.term } : {}),
                    ...(page.cursor !== undefined ? { cursor: page.cursor } : {}),
                    ...(page.pageSize !== undefined ? { pageSize: page.pageSize } : {}),
                } as never,
            })),

        describeRemote: async (id: string, path: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/oci/registries/{registry}/actions/describeRemote", {
                params: { path: { registry: id } },
                body: { path } as never,
            })),
    };



    // OCI repositories (Annex Q): the remote-tracking branches. `register`
    // downloads nothing; `refresh` is the "git fetch" (202 Task — resolutions
    // + reflog land on the observed group); `pull` resolves NOW and downloads
    // THAT digest into the cluster cache (202 Task, 422 while no cache).
    const ociRepositories = {
        list: async (p: ListParams<FilterableFields["listOciRepositories"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/oci/repositories", { params: { query: listQuery(p) as never } }), zOciRepository),

        listAll: (p: Omit<ListParams<FilterableFields["listOciRepositories"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => ociRepositories.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/oci/repositories/{repository}", { params: { path: { repository: id } } }), zOciRepository),

        create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/oci/repositories", { params: { query: writeQuery(opts) as never }, body: body as never }), zOciRepository),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/oci/repositories/{repository}", {
                params: { path: { repository: id }, query: writeQuery(opts) as never },
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zOciRepository),

        // `purge` confirms evicting the repository's downloaded (orphan)
        // image records (Annex Q wave 2 — required when they exist and no app
        // consumes them: `purge_required` otherwise).
        delete: async (id: string, opts: { purge?: boolean } = {}): Promise<void> => {
            unwrap(await transport.DELETE("/api/v6/oci/repositories/{repository}", {
                params: { path: { repository: id }, query: (opts.purge === true ? { purge: true } : {}) as never },
            }));
        },

        batchDelete: async (ids: string[]): Promise<BatchDeleteResult> =>
            unwrap(await transport.DELETE("/api/v6/oci/repositories/batch", { body: { ids } as never }), zBatchDeleteResult),

        refresh: async (id: string, branch?: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/oci/repositories/{repository}/actions/refresh", {
                params: { path: { repository: id } },
                body: (branch !== undefined ? { branch } : {}) as never,
            })),

        pull: async (id: string, ref: { branch?: string; digest?: string }): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/oci/repositories/{repository}/actions/pull", {
                params: { path: { repository: id } },
                body: ref as never,
            })),
    };



    // Pods (round «Pod nativi»): the container TEMPLATE with replicas —
    // the backend materializes the instances (Apps). `create` answers 201 +
    // X-TVirt-Task (the parent of the per-instance converges); `patch` is
    // also the SCALE (`replicas`); the lifecycle actions answer 202 with the
    // parent task whose children are the per-instance tasks, gated in
    // sequence (the restart IS a rolling restart).
    const pods = {
        list: async (p: ListParams<FilterableFields["listPods"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/pods", { params: { query: listQuery(p) as never } }), zPod),

        listAll: (p: Omit<ListParams<FilterableFields["listPods"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => pods.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/pods/{pod}", { params: { path: { pod: id } } }), zPod),

        create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<WriteResult<Record<string, unknown>>> =>
            writeResult(await transport.POST("/api/v6/pods", { params: { query: writeQuery(opts) as never }, body: body as never }), zPod),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/pods/{pod}", {
                params: { path: { pod: id }, query: writeQuery(opts) as never },
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zPod),

        // Delete answers 202 + the parent task (one app.delete child per
        // instance, then the pod.delete that kills the row). `purge` is the
        // persistent-data choice, per instance — OMITTED when an instance has
        // persistent pieces the API refuses speaking (409 listing them).
        delete: async (id: string, opts: { purge?: boolean } = {}): Promise<{ task: Task; keptData: { app?: string | undefined; name: string; storage: string; path: string }[] }> =>
            unwrap(await transport.DELETE("/api/v6/pods/{pod}", {
                params: { path: { pod: id }, query: (opts.purge !== undefined ? { purge: opts.purge } : {}) as never },
            }), zPodDeleteResult),

        start: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/pods/{pod}/actions/start", { params: { path: { pod: id } } })),

        stop: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/pods/{pod}/actions/stop", { params: { path: { pod: id } } })),

        restart: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/pods/{pod}/actions/restart", { params: { path: { pod: id } } })),

        refresh: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/pods/{pod}/actions/refresh", { params: { path: { pod: id } } })),
    };



    // Apps: the INSTANCES of a Pod (round «Pod nativi»; Annex R's managed
    // container before it). Read-only but for `notes`; no create (the Pod
    // materializes them) and no delete (scale the Pod, or delete it); the
    // lifecycle actions answer 202 Task on the instance's node; `move`
    // writes the authority synchronously (200 + the App, `warnings` when a
    // lockfile was re-resolved); `logs` enqueues the capture task of ONE
    // container whose frames carry the output (tasks.log reads them).
    const apps = {
        list: async (p: ListParams<FilterableFields["listApps"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/apps", { params: { query: listQuery(p) as never } }), zApp),

        listAll: (p: Omit<ListParams<FilterableFields["listApps"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => apps.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/apps/{app}", { params: { path: { app: id } } }), zApp),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/apps/{app}", {
                params: { path: { app: id }, query: writeQuery(opts) as never },
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zApp),

        start: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/apps/{app}/actions/start", { params: { path: { app: id } } })),

        stop: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/apps/{app}/actions/stop", { params: { path: { app: id } } })),

        // Whole pod without options; `container` = the PARTIAL restart of one
        // member inside the live pod (the infra and the sisters untouched).
        restart: async (id: string, opts: { container?: string } = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/apps/{app}/actions/restart", {
                params: { path: { app: id } },
                body: (opts.container !== undefined ? { container: opts.container } : {}) as never,
            })),

        refresh: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/apps/{app}/actions/refresh", { params: { path: { app: id } } })),

        move: async (id: string, node: string | Record<string, string>, opts: WriteOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/apps/{app}/actions/move", {
                params: { path: { app: id }, query: writeQuery(opts) as never },
                body: { node } as never,
            }), zApp),

        logs: async (id: string, opts: { tail?: number; container?: string } = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/apps/{app}/actions/logs", {
                params: { path: { app: id } },
                body: {
                    ...(opts.tail !== undefined ? { tail: opts.tail } : {}),
                    ...(opts.container !== undefined ? { container: opts.container } : {}),
                } as never,
            })),

        // The exec session (round #98) — the `cts.console` idiom: this mints
        // the ticket and hands back the WebSocket URL; authorization happens
        // HERE, and the URL carries the whole credential. There is
        // deliberately no wrapper for the bridge itself — it is a WebSocket,
        // not a REST call. `mode: "pipe"` needs `argv`; `mode: "tty"` defaults
        // to /bin/sh and takes the terminal's geometry so the pty is born the
        // right size; `container` picks the template container (default the
        // first non-init one).
        exec: async (id: string, body: { mode: "tty" | "pipe"; container?: string; argv?: string[]; rows?: number; cols?: number }): Promise<{ execWebSocketUrl: string; mode: "tty" | "pipe" }> =>
            unwrap(await transport.POST("/api/v6/apps/{app}/actions/exec", {
                params: { path: { app: id } },
                body: body as never,
            }), zAppExecAccess),
    };



    // OCI images (Annex Q, collector model ratified 2026-08-05): an image IS
    // an observed branch — the collector of the digests that branch
    // materialized in the cluster cache. Governed like every config resource
    // (create = observe the branch, patch = notes, delete = stop observing,
    // guarded); the digests inside are observed content, and freeing their
    // disk is the explicit eviction on the repository (evictDigest — there is
    // no automatic per-digest retention). Plus the catalog import action
    // (#88): materialize a READY catalog oci-image archive (202 Task — the
    // provenance-'catalog' repository with the synthetic
    // catalog/<catalogName>/<fileName> reference is minted or reused by the
    // action itself).
    const ociBranches = {
        list: async (p: ListParams<FilterableFields["listOciBranches"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/oci/branches", { params: { query: listQuery(p) as never } }), zOciBranch),

        listAll: (p: Omit<ListParams<FilterableFields["listOciBranches"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => ociBranches.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/oci/branches/{branch}", { params: { path: { branch: id } } }), zOciBranch),

        // Observing a branch already observed is the SAME promotion, not a
        // conflict (the register precedent): 200 with the existing branch.
        create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/oci/branches", { params: { query: writeQuery(opts) as never }, body: body as never }), zOciBranch),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/oci/branches/{branch}", {
                params: { path: { branch: id }, query: writeQuery(opts) as never },
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zOciBranch),

        // `purge` confirms letting the images this branch alone names stay in
        // the cache with no name. Deleting a branch never removes bytes: only
        // the last VIEW of an image does that (R46).
        delete: async (id: string, opts: { purge?: boolean } = {}): Promise<void> => {
            unwrap(await transport.DELETE("/api/v6/oci/branches/{branch}", {
                params: { path: { branch: id }, query: (opts.purge === true ? { purge: true } : {}) as never },
            }));
        },
    };



    // The IMAGE is the DIGEST (R39): the content itself, addressable again as
    // it was in wave 1. `delete` is `docker image rm` — it removes the
    // caller's scope's VIEW, and with the last view the bytes go (R46), which
    // is why it can answer either 204 (view dropped) or 202 (blob removal
    // task). `force` confirms removing the head of a branch; it never
    // bypasses an app's `in_use` (R44.1).
    const ociImages = {
        list: async (p: ListParams<FilterableFields["listOciImages"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/oci/images", { params: { query: listQuery(p) as never } }), zOciImage),

        listAll: (p: Omit<ListParams<FilterableFields["listOciImages"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => ociImages.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/oci/images/{image}", { params: { path: { image: id } } }), zOciImage),

        delete: async (id: string, opts: { force?: boolean } = {}): Promise<Record<string, unknown> | undefined> =>
            unwrap(await transport.DELETE("/api/v6/oci/images/{image}", {
                params: { path: { image: id }, query: (opts.force === true ? { force: true } : {}) as never },
            })),

        importFromCatalog: async (file: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/oci/images/actions/importFromCatalog", { body: { file } as never })),
    };



    // I CLAIM (#370): il riferimento di uno scope sopravvissuto al contenuto —
    // cio' che una view diventa quando l'immagine smette di essere
    // materializzata (la 0206, e `storages.delete(id, { releaseImages: true })`).
    // Non scadono di proposito: sono l'unica informazione che una ri-adozione
    // non potrebbe ricostruire, quindi a farli sparire e' un gesto, mai un
    // timer. `drop` toglie i claim DEI PROPRI scope, come la delete di
    // un'immagine toglie la propria view.
    const ociViewClaims = {
        list: async (p: { limit?: number; cursor?: string | null } = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/oci/claims", { params: { query: listQuery(p) as never } }), zOciViewClaim),

        listAll: (): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => ociViewClaims.list({ cursor })),

        drop: async (digest: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v6/oci/claims/{digest}", { params: { path: { digest } } }));
        },
    };



    // Backup destinations (dettatura Luca 2026-08-06, task #278): WHERE the
    // platform writes what it must be able to bring back — config CRUD plus
    // the "create a checkpoint now" action (→ 202 Task). The destination is
    // shared from birth with the data backups of a later round; `contents`
    // declares what it accepts.
    const backupDestinations = {
        list: async (p: ListParams<FilterableFields["listBackupDestinations"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/backupDestinations", { params: { query: listQuery(p) as never } }), zBackupDestination),

        listAll: (p: Omit<ListParams<FilterableFields["listBackupDestinations"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => backupDestinations.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/backupDestinations/{destination}", { params: { path: { destination: id } } }), zBackupDestination),

        create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/backupDestinations", { params: { query: writeQuery(opts) as never }, body: body as never }), zBackupDestination),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/backupDestinations/{destination}", {
                params: { path: { destination: id }, query: writeQuery(opts) as never },
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zBackupDestination),

        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v6/backupDestinations/{destination}", { params: { path: { destination: id } } }));
        },

        batchDelete: async (ids: string[]): Promise<BatchDeleteResult> =>
            unwrap(await transport.DELETE("/api/v6/backupDestinations/batch", { body: { ids } as never }), zBatchDeleteResult),

        // "Crea checkpoint": the masterpass protects keystore secret values
        // (argon2id → AES-256-GCM per value); omitted = placeholders.
        createCheckpoint: async (id: string, masterpass?: string): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/backupDestinations/{destination}/actions/createCheckpoint", {
                params: { path: { destination: id } },
                body: (masterpass !== undefined ? { masterpass } : {}) as never,
            }), zTask),

        // BK7 / PROPOSAL 22 — the catalog re-minted from the destination's
        // own artefacts (manifest-driven; existing rows never touched).
        rescan: async (id: string): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/backupDestinations/{destination}/actions/rescan", {
                params: { path: { destination: id } },
            }), zTask),
    };



    // Checkpoints (backup-config-round1-design.md, ratified 2026-07-27 as
    // "backup round 1"; renamed configArchives and then checkpoints on
    // 2026-08-06): the append-only records of the declared configuration
    // (+ artefact download), and the UPLOAD-first restore flow — stage,
    // chunked content PUT (the catalogFiles.uploadContent mechanics), the
    // explorer on GET, masterpass check, restore → 202 Task.
    const checkpoints = {
        list: async (p: ListParams<FilterableFields["listCheckpoints"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/checkpoints", { params: { query: listQuery(p) as never } }), zCheckpoint),

        listAll: (p: Omit<ListParams<FilterableFields["listCheckpoints"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => checkpoints.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/checkpoints/{checkpoint}", { params: { path: { checkpoint: id } } }), zCheckpoint),

        delete: async (id: string): Promise<Task> =>
            unwrap(await transport.DELETE("/api/v6/checkpoints/{checkpoint}", { params: { path: { checkpoint: id } } }), zTask),

        // The single-file artefact, raw bytes (browser callers usually
        // prefer a plain <a href> to the same URL).
        downloadContent: async (id: string): Promise<Uint8Array> => {
            const response = await fetchImpl(`${options.baseUrl}/api/v6/checkpoints/${id}/content`, {
                method: "GET",
                headers: authHeaders(),
            });
            if (!response.ok) {
                throw new TVirtApiError(response.status, (await response.json().catch(() => ({}))) as Record<string, unknown>);
            }
            return new Uint8Array(await response.arrayBuffer());
        },

        restore: {
            createUpload: async (): Promise<Record<string, unknown>> =>
                unwrap(await transport.POST("/api/v6/checkpoints/restore/uploads", {}), zConfigRestoreUpload),

            getUpload: async (id: string): Promise<Record<string, unknown>> =>
                unwrap(await transport.GET("/api/v6/checkpoints/restore/uploads/{upload}", { params: { path: { upload: id } } }), zConfigRestoreUpload),

            deleteUpload: async (id: string): Promise<void> => {
                unwrap(await transport.DELETE("/api/v6/checkpoints/restore/uploads/{upload}", { params: { path: { upload: id } } }));
            },

            // The catalogFiles.uploadContent mechanics verbatim (chunks sized under
            // the proxy body threshold, 409 → offset resync, and since #257 the
            // same two anti-truncation guards — a short chunk and an early
            // finalize both stop the upload); the final chunk answers the
            // ConfigRestoreUpload — phase `ready` with the explorer, or `error`
            // with the reason verbatim.
            uploadContent: async (
                id: string,
                content: ReadableStream<Uint8Array> | Uint8Array | Blob | UploadSource,
                opts: { chunkBytes?: number; onProgress?: (sentBytes: number, totalBytes: number) => void } = {},
            ): Promise<Record<string, unknown>> => {
                const url = `${options.baseUrl}/api/v6/checkpoints/restore/uploads/${id}/content`;
                if (content instanceof ReadableStream) {
                    const response = await fetchImpl(url, {
                        method: "PUT",
                        headers: authHeaders({ "content-type": "application/octet-stream" }),
                        body: content,
                        duplex: "half",
                    });
                    if (!response.ok) {
                        throw new TVirtApiError(response.status, (await response.json().catch(() => ({}))) as Record<string, unknown>);
                    }
                    return (await response.json()) as Record<string, unknown>;
                }
                const source = uploadSourceOf(content);
                const total = source.sizeBytes;
                const chunkBytes = opts.chunkBytes ?? 8 * 1024 * 1024;
                let offset = 0;
                let resyncs = 0;
                for (;;) {
                    const end = Math.min(offset + chunkBytes, total);
                    const response = await fetchImpl(url, {
                        method: "PUT",
                        headers: authHeaders({
                            "content-type": "application/octet-stream",
                            "content-range": `bytes ${offset}-${Math.max(end - 1, 0)}/${total}`,
                        }),
                        body: await readUploadChunk(source, offset, end),
                    });
                    if (response.status === 409) {
                        const expected = Number(response.headers.get("x-tvirt-upload-offset"));
                        if (Number.isFinite(expected) && expected !== offset && resyncs++ < 3) {
                            await response.body?.cancel().catch(() => undefined);
                            offset = expected;
                            continue;
                        }
                        throw new TVirtApiError(409, (await response.json().catch(() => ({}))) as Record<string, unknown>);
                    }
                    if (!response.ok) {
                        throw new TVirtApiError(response.status, (await response.json().catch(() => ({}))) as Record<string, unknown>);
                    }
                    offset = end;
                    opts.onProgress?.(offset, total);
                    if (response.status === 200) {
                        if (offset < total) {
                            throw new TVirtApiError(500, {
                                title: "Upload finalized early",
                                detail: `the server finalized after ${offset} of ${total} bytes — the staged checkpoint is incomplete`,
                            });
                        }
                        return (await response.json()) as Record<string, unknown>;
                    }
                    if (offset >= total) {
                        throw new TVirtApiError(500, { title: "Upload finalize missing", detail: "the final chunk did not open the artefact" });
                    }
                }
            },

            // B15 — the restore PREVIEW: the selection (the restore body's
            // array, JSON-encoded into the query by this method) compared
            // per object against the live state. Omit the selection for the
            // whole artefact. A pure read: call it as often as the operator
            // reshapes the selection — the apply's admission re-checks
            // everything regardless (#294).
            diff: async (id: string, selection?: { family: string; objects?: { table: string; key: string }[] }[]): Promise<Record<string, unknown>> =>
                unwrap(await transport.GET("/api/v6/checkpoints/restore/uploads/{upload}/diff", {
                    params: {
                        path: { upload: id },
                        query: selection !== undefined ? { selection: JSON.stringify(selection) } : {},
                    },
                }), zConfigRestoreDiff),

            checkMasterpass: async (id: string, masterpass: string): Promise<{ valid: boolean }> =>
                unwrap(await transport.POST("/api/v6/checkpoints/restore/uploads/{upload}/actions/checkMasterpass", {
                    params: { path: { upload: id } },
                    body: { masterpass } as never,
                })),

            restore: async (id: string, body: {
                mode: "merge" | "overwrite";
                selection: { family: string; objects?: { table: string; key: string }[] }[];
                masterpass?: string;
                parachuteDestination?: string;
            }): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/checkpoints/restore/uploads/{upload}/actions/restore", {
                    params: { path: { upload: id } },
                    body: body as never,
                }), zTask),
        },
    };

    // VM data backup primitives (#235 Rounds A/B/D; the noun «backup» is VM
    // DATA per the #236 ratifica): the naked .tvtpl description, per-disk
    // change tracking (persistent QEMU dirty bitmaps — enable/disable/reset
    // are 202 Tasks, the read is the node's last observation plus the epoch
    // ledger and its changeIds since round D), the read-only NBD export
    // sessions (Round B landed the node-side server; round D serves rbd and
    // SAN too), and the changed-areas query of rbd/SAN disks (a 202 Task
    // whose `result` carries the page — the round-#91 query channel).
    const backup = {
        description: async (vm: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/vms/{vm}/description", { params: { path: { vm } } }), zVmDescription),

        changeTracking: {
            get: async (vm: string, disk: number): Promise<Record<string, unknown>> =>
                unwrap(await transport.GET("/api/v6/vms/{vm}/disks/{disk}/changeTracking", {
                    params: { path: { vm, disk: String(disk) } },
                }), zVmDiskChangeTracking),

            enable: async (vm: string, disk: number, body: { name: string; granularityBytes?: number }): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/vms/{vm}/disks/{disk}/actions/enableChangeTracking", {
                    params: { path: { vm, disk: String(disk) } },
                    body: body as never,
                }), zTask),

            disable: async (vm: string, disk: number, name: string): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/vms/{vm}/disks/{disk}/actions/disableChangeTracking", {
                    params: { path: { vm, disk: String(disk) } },
                    body: { name } as never,
                }), zTask),

            reset: async (vm: string, disk: number, name: string): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/vms/{vm}/disks/{disk}/actions/resetChangeTracking", {
                    params: { path: { vm, disk: String(disk) } },
                    body: { name } as never,
                }), zTask),
        },

        // The changed-areas query (round D, T235-P4): snapshot-milestone
        // disks only; the page lands in the Task `result` (VmChangedAreasPage)
        // — `tasks.wait` and read `result`, then repeat with `startOffset =
        // nextOffset` until absent.
        changedAreas: {
            query: async (vm: string, disk: number, body: { from: string; to: string; startOffset?: number; limit?: number }): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/vms/{vm}/disks/{disk}/actions/queryChangedAreas", {
                    params: { path: { vm, disk: String(disk) } },
                    body: body as never,
                }), zTask),
        },

        exports: {
            create: async (vm: string, body: { disk: number; snapshot: string; bitmap?: string; ttlMinutes?: number }): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/vms/{vm}/actions/backupExport", {
                    params: { path: { vm } },
                    body: body as never,
                }), zTask),

            list: async (p: ListParams<FilterableFields["listBackupExports"]> = {}): Promise<Page<Record<string, unknown>>> =>
                page(await transport.GET("/api/v6/backupExports", { params: { query: listQuery(p) as never } }), zBackupExport),

            listAll: (p: Omit<ListParams<FilterableFields["listBackupExports"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
                genPageIterator(async ({ cursor }) => backup.exports.list({ ...p, cursor })),

            get: async (id: string): Promise<Record<string, unknown>> =>
                unwrap(await transport.GET("/api/v6/backupExports/{export}", { params: { path: { export: id } } }), zBackupExport),

            close: async (id: string): Promise<Task> =>
                unwrap(await transport.DELETE("/api/v6/backupExports/{export}", { params: { path: { export: id } } }), zTask),
        },

        // The WRITE door (round D4, T235-P6): a writable session on the live
        // head of a VIRGIN disk of a stopped VM — the restore's data path. One
        // resource with `exports` (list/get/close are the same, the row reads
        // `mode: write`), a verb and a permission of its own (vm.restore.data).
        restoreExports: {
            create: async (vm: string, body: { disk: number; ttlMinutes?: number }): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/vms/{vm}/actions/restoreExport", {
                    params: { path: { vm } },
                    body: body as never,
                }), zTask),
        },

        // T235-P7 (round D4): the naked description comes back and mints a
        // stopped VM with empty disks of the described geometry — the 202 is
        // the provisioning parent task whose `target` is the newborn VM.
        createVmFromDescription: async (body: { description: Record<string, unknown>; name: string; scope?: string; storage: string; metadataStorage?: string; bridge?: string; network?: string }): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/vms/actions/createFromDescription", { body: body as never }), zTask),
    };



    // VM data backups (#28 — the integrated backup ENGINE, dettature Luca
    // 2026-08-06/07). The other half of `backup` above: those are the
    // primitives an external product builds on, these are the «backup
    // minimali senza software esterni» TVirt ships. `create` takes the FULL
    // of a STOPPED VM onto a BackupDestination that accepts `backups` (→ 202
    // Task, executed on the node hosting the VM); the records are append-only
    // and carry the artefact's own manifest — one sha256 per file, the
    // directory readable with qemu-img/tar/jq alone.
    const vmBackups = {
        // BK2: `requireQuiesce` is the hot path's one knob (PROPOSAL 7) — a
        // running VM backs up through the marked temporary snapshot, and the
        // flag turns a silent crash-consistent downgrade into a speaking
        // failure. BK3: `mode: "incremental"` rides the backup series
        // (PROPOSAL 8+9) — a qcow2 delta on the previous backup, or a
        // DECLARED full that seeds/re-seeds the series when it cannot be
        // ridden (the honest degrade).
        create: async (vm: string, destination: string, opts: { requireQuiesce?: boolean; mode?: "full" | "incremental" } = {}): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/vms/{vm}/actions/createBackup", {
                params: { path: { vm } },
                body: {
                    destination,
                    ...(opts.requireQuiesce !== undefined ? { requireQuiesce: opts.requireQuiesce } : {}),
                    ...(opts.mode !== undefined ? { mode: opts.mode } : {}),
                } as never,
            }), zTask),

        list: async (p: ListParams<FilterableFields["listVmBackups"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/vmBackups", { params: { query: listQuery(p) as never } }), zVmBackup),

        listAll: (p: Omit<ListParams<FilterableFields["listVmBackups"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => vmBackups.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/vmBackups/{vmBackup}", { params: { path: { vmBackup: id } } }), zVmBackup),

        delete: async (id: string): Promise<Task> =>
            unwrap(await transport.DELETE("/api/v6/vmBackups/{vmBackup}", { params: { path: { vmBackup: id } } }), zTask),

        // THE TWO GESTURES, and they are two methods for the same reason they
        // are two operations: one mints a machine (fresh id, fresh MACs), the
        // other replaces the data of a machine that already exists and keeps
        // everything that identifies it. A `mode` argument would put them one
        // typo apart.
        restoreToNewVm: async (id: string, body: { name: string; scope?: string; storage: string; metadataStorage?: string; bridge?: string }): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/vmBackups/{vmBackup}/actions/restoreToNewVm", {
                params: { path: { vmBackup: id } },
                body: body as never,
            }), zTask),

        restoreOver: async (id: string, vm: string): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/vmBackups/{vmBackup}/actions/restoreOver", {
                params: { path: { vmBackup: id } },
                body: { vm } as never,
            }), zTask),
    };

    // BK4 — the sibling kinds (PROPOSAL 10+11, ratified 2026-08-24): the
    // vmBackups group's shape for CTs and Apps. No knobs on create by design
    // (the artefact's shape is not an operator choice; consistency follows
    // the object's own power state — stopped, or frozen by the cgroup
    // freezer for the pack). Two restore gestures, two methods, same reason.
    const ctBackups = {
        create: async (ct: string, destination: string): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/cts/{ct}/actions/createBackup", {
                params: { path: { ct } },
                body: { destination } as never,
            }), zTask),

        list: async (p: ListParams<FilterableFields["listCtBackups"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/ctBackups", { params: { query: listQuery(p) as never } }), zCtBackup),

        listAll: (p: Omit<ListParams<FilterableFields["listCtBackups"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => ctBackups.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/ctBackups/{ctBackup}", { params: { path: { ctBackup: id } } }), zCtBackup),

        delete: async (id: string): Promise<Task> =>
            unwrap(await transport.DELETE("/api/v6/ctBackups/{ctBackup}", { params: { path: { ctBackup: id } } }), zTask),

        restoreToNewCt: async (id: string, body: { name: string; scope?: string; storage: string; bridge?: string; network?: string }): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/ctBackups/{ctBackup}/actions/restoreToNewCt", {
                params: { path: { ctBackup: id } },
                body: body as never,
            }), zTask),

        restoreOver: async (id: string, ct: string): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/ctBackups/{ctBackup}/actions/restoreOver", {
                params: { path: { ctBackup: id } },
                body: { ct } as never,
            }), zTask),
    };

    const appBackups = {
        create: async (appId: string, destination: string): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/apps/{app}/actions/createBackup", {
                params: { path: { app: appId } },
                body: { destination } as never,
            }), zTask),

        list: async (p: ListParams<FilterableFields["listAppBackups"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/appBackups", { params: { query: listQuery(p) as never } }), zAppBackup),

        listAll: (p: Omit<ListParams<FilterableFields["listAppBackups"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => appBackups.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/appBackups/{appBackup}", { params: { path: { appBackup: id } } }), zAppBackup),

        delete: async (id: string): Promise<Task> =>
            unwrap(await transport.DELETE("/api/v6/appBackups/{appBackup}", { params: { path: { appBackup: id } } }), zTask),

        restoreToNewApp: async (id: string, body: { name: string; scope?: string; node?: string; storage?: string }): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/appBackups/{appBackup}/actions/restoreToNewApp", {
                params: { path: { appBackup: id } },
                body: body as never,
            }), zTask),

        restoreOver: async (id: string, appId: string): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/appBackups/{appBackup}/actions/restoreOver", {
                params: { path: { appBackup: id } },
                body: { app: appId } as never,
            }), zTask),
    };

    // Managed Ceph (Annex N, wave A — ratified 2026-07-24; birth lifecycle
    // ratified the same day): a collection of INDEPENDENT CephClusters. POST
    // BIRTHS the fabric (201 + the automatic bootstrap parent Task in
    // X-TVirt-Task: mon+mgr on the required controlNode, addressed by the
    // first-class network subnets); `deploy` is the explicit RETRY of a failed
    // bootstrap (and the re-birth after destroy). Additional mon membership
    // and the OSD device registry move through actions, never field writes —
    // a device registered on the LIVE cluster is prepared automatically.
    // deploy/addMon/removeMon/destroy and DELETE are sudo-protected
    // (auth.sudo first).
    const hci = {
        cephClusters: {
            list: async (p: ListParams<FilterableFields["listCephClusters"]> = {}): Promise<Page<Record<string, unknown>>> =>
                page(await transport.GET("/api/v6/hci/cephClusters", { params: { query: listQuery(p) as never } }), zCephCluster),

            listAll: (p: Omit<ListParams<FilterableFields["listCephClusters"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
                genPageIterator(async ({ cursor }) => hci.cephClusters.list({ ...p, cursor })),

            get: async (id: string): Promise<Record<string, unknown>> =>
                unwrap(await transport.GET("/api/v6/hci/cephClusters/{cephCluster}", { params: { path: { cephCluster: id } } }), zCephCluster),

            create: async (body: Record<string, unknown>): Promise<Record<string, unknown>> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters", { body: body as never }), zCephCluster),

            patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
                unwrap(await transport.PATCH("/api/v6/hci/cephClusters/{cephCluster}", {
                    params: { path: { cephCluster: id } },
                    body: mergePatch as never,
                    headers: mergePatchHeaders(opts),
                }), zCephCluster),

            // Config-only and legal only on a not-deployed object (ratified D7):
            // a deployed fabric goes through `destroy` first — 409 otherwise.
            delete: async (id: string): Promise<void> => {
                unwrap(await transport.DELETE("/api/v6/hci/cephClusters/{cephCluster}", { params: { path: { cephCluster: id } } }));
            },

            deploy: async (id: string): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/deploy", { params: { path: { cephCluster: id } } }), zTask),

            // The N.3 per-node address model (ratified 2026-07-24): addMon
            // collects the node's addresses on the cluster's networks —
            // `address` (public; optional while a declaration/fallback covers
            // the node), `clusterAddress` (required when the cluster network
            // is separate and undeclared for the node).
            addMon: async (id: string, node: string, addresses: { address?: string; clusterAddress?: string } = {}): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/addMon", { params: { path: { cephCluster: id } }, body: { node, ...addresses } as never }), zTask),

            removeMon: async (id: string, node: string): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/removeMon", { params: { path: { cephCluster: id } }, body: { node } as never }), zTask),

            // Split mon/mgr (Avanzate — the D1 relaxation, ratified 2026-07-24):
            // legal only while advanced.splitMonMgr is on (422 otherwise); sudo,
            // node-affine task, 409 on the last mgr.
            // A mgr speaks on the PUBLIC network only: `address` rides the
            // addMon semantics, there is no cluster-network address for it.
            addMgr: async (id: string, node: string, addresses: { address?: string } = {}): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/addMgr", { params: { path: { cephCluster: id } }, body: { node, ...addresses } as never }), zTask),

            removeMgr: async (id: string, node: string): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/removeMgr", { params: { path: { cephCluster: id } }, body: { node } as never }), zTask),

            // MDS membership (MDS/CephFS round, ratified 2026-07-24): the addMgr
            // mechanics — sudo, node-affine task, public-side address only. The
            // MDS membership is its own from the first day (born empty);
            // removeMds answers 409 on the last MDS while a filesystem exists.
            addMds: async (id: string, node: string, addresses: { address?: string } = {}): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/addMds", { params: { path: { cephCluster: id } }, body: { node, ...addresses } as never }), zTask),

            removeMds: async (id: string, node: string): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/removeMds", { params: { path: { cephCluster: id } }, body: { node } as never }), zTask),

            // CephFS filesystems (same round): sudo, deployed clusters only, 202
            // + Task on a mon-hosting node. createFs births the data/metadata
            // pools (cephfs.<name>.data / .meta) and runs `fs new` — MDS FIRST
            // (422 without a declared MDS); removeFs is confirm-carrying
            // (confirm = the fs name), refuses 409 in_use while a driver:
            // cephfs Storage consumes the filesystem, and deletes the fs's own
            // pools with it.
            createFs: async (id: string, body: { name: string; size?: number; minSize?: number }): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/createFs", { params: { path: { cephCluster: id } }, body: body as never }), zTask),

            removeFs: async (id: string, body: { name: string; confirm: string }): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/removeFs", { params: { path: { cephCluster: id } }, body: body as never }), zTask),

            // The manual OSD-flag override (Avanzate): sudo, deployed clusters
            // only (409 otherwise); the observed osdFlags follow through the
            // status sweep. The STANDARD flows manage the flags on their own in
            // the normal cases — this is the operator's explicit deviation.
            setOsdFlag: async (id: string, flag: string, value: boolean): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/setOsdFlag", { params: { path: { cephCluster: id } }, body: { flag, value } as never }), zTask),

            // Registry write (+ the automatic prepare task on a live cluster).
            // The N.3 address pair rides here too (2026-07-24): an OSD host
            // participates on both networks — address (public) optional while
            // a declaration/fallback covers the node, clusterAddress required
            // when the cluster network is separate and undeclared. `encrypted`
            // is the per-OSD choice (round #90): legal only under
            // osdEncryptionMode perOsd (422 speaking otherwise), server
            // default true when omitted there.
            registerOsdDevice: async (id: string, body: { node: string; address?: string; clusterAddress?: string; encrypted?: boolean; device: { name: string; serial: string } }): Promise<Record<string, unknown>> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/registerOsdDevice", { params: { path: { cephCluster: id } }, body: body as never }), zCephCluster),

            unregisterOsdDevice: async (id: string, body: { node: string; device: { name: string; serial?: string } }): Promise<Record<string, unknown>> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/unregisterOsdDevice", { params: { path: { cephCluster: id } }, body: body as never }), zCephCluster),

            // Batch 2026-07-26 (items 11/13): the OSD gestures and the pool
            // knobs — task-backed, no sudo.
            updatePool: async (id: string, body: { name: string; size?: number; minSize?: number; pgNum?: number; pgAutoscaleMode?: string }): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/updatePool", { params: { path: { cephCluster: id } }, body: body as never }), zTask),

            osdIn: async (id: string, osdId: number): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/osdIn", { params: { path: { cephCluster: id } }, body: { osdId } as never }), zTask),

            osdOut: async (id: string, osdId: number): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/osdOut", { params: { path: { cephCluster: id } }, body: { osdId } as never }), zTask),

            osdScrub: async (id: string, osdId: number): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/osdScrub", { params: { path: { cephCluster: id } }, body: { osdId } as never }), zTask),

            osdDeepScrub: async (id: string, osdId: number): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/osdDeepScrub", { params: { path: { cephCluster: id } }, body: { osdId } as never }), zTask),

            restartDaemon: async (id: string, body: { daemon: string; node?: string }): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/restartDaemon", { params: { path: { cephCluster: id } }, body: body as never }), zTask),

            // Annex N.4 consumption (ceph-rbd 6.1 wave A, ratified 2026-07-24;
            // extended to cephfs with the MDS/CephFS round): PROPOSE, never
            // auto-create — this explicit POST births the consuming Storage
            // (rbd: pool ensure + pool-capped cephx + storage row + init;
            // cephfs: fs-capped cephx + mounted dir namespace, the filesystem
            // must already exist) on a healthy cluster. 202 + Task; the task
            // params carry the pre-minted storageId. driver defaults to rbd
            // server-side.
            proposeStorage: async (id: string, body: { driver?: "rbd" | "cephfs"; pool?: string; fs?: string; path?: string; name: string }): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/proposeStorage", { params: { path: { cephCluster: id } }, body: body as never }), zTask),

            // First-class pools (ratified 2026-07-24, round #89): sudo, deployed
            // clusters only (422 otherwise), 202 + Task on a mon-hosting node.
            // createPool applies size (server default 3) and minSize (server
            // default max(1, size - 1)) and enables the rbd application;
            // deletePool is confirm-carrying (confirm = the pool's name) and
            // refuses 409 in_use while a driver: rbd Storage consumes the pool.
            createPool: async (id: string, body: { name: string; size?: number; minSize?: number }): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/createPool", { params: { path: { cephCluster: id } }, body: body as never }), zTask),

            deletePool: async (id: string, body: { name: string; confirm: string }): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/deletePool", { params: { path: { cephCluster: id } }, body: body as never }), zTask),

            // The CRUSH editor, simple half (round #92 — Annex N.13): sudo,
            // deployed clusters only, 202 + Task on a mon-hosting node under
            // the per-cluster lease. createCrushRule births a replicated rule
            // with ONE failure-domain choice (rack is guarded 422 while the
            // observed hierarchy carries no rack buckets); deleteCrushRule is
            // confirm-carrying (confirm = the rule's name), refuses 409
            // in_use while an observed pool rides the rule and 422 on the
            // builtin replicated_rule; setPoolCrushRule points a pool at an
            // observed, satisfiable rule (`osd pool set crush_rule`).
            createCrushRule: async (id: string, body: { name: string; failureDomain: "osd" | "host" | "rack" }): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/createCrushRule", { params: { path: { cephCluster: id } }, body: body as never }), zTask),

            deleteCrushRule: async (id: string, body: { name: string; confirm: string }): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/deleteCrushRule", { params: { path: { cephCluster: id } }, body: body as never }), zTask),

            setPoolCrushRule: async (id: string, body: { pool: string; rule: string }): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/setPoolCrushRule", { params: { path: { cephCluster: id } }, body: body as never }), zTask),

            // The CRUSH editor, raw half (round #92): queryCrushmap rides the
            // round-#91 query channel — 202 + a lease-free ceph.query task
            // whose result carries {kind: "crushmap", text} (the `crushtool
            // -d` source text); feed the ref to tasks.wait and read result
            // there. setCrushmap is the guarded inject: sudo, confirm = the
            // CLUSTER's name, and the worker compiles (`crushtool -c`) and
            // test-runs (`crushtool --test`) BEFORE `osd setcrushmap` —
            // compile/test failures land in the task error, speaking.
            queryCrushmap: async (id: string): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/queryCrushmap", { params: { path: { cephCluster: id } } }), zTask),

            setCrushmap: async (id: string, body: { text: string; confirm: string }): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/setCrushmap", { params: { path: { cephCluster: id } }, body: body as never }), zTask),

            // The read-only object query (round #91 — PROPOSAL Annex N.12):
            // 202 + a lease-free `ceph.query` task on a mon-hosting node; the
            // STRUCTURED answer lands in the task's `result` — feed the
            // returned ref to tasks.wait and read result there. Depth by
            // presence: pool alone lists the RBD images; pool+image answers
            // the image detail (info/du/watchers/snapshots + the sampled OSD
            // placement summary); pool+object maps one RADOS object
            // (`ceph osd map`). image and object together are 422.
            queryObjects: async (id: string, body: { pool: string; image?: string; object?: string }): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/queryObjects", { params: { path: { cephCluster: id } }, body: body as never }), zTask),

            // The explicit teardown: `confirm` must equal the cluster's name and
            // `wipeDevices` is MANDATORY with no default (the contract's honesty
            // about byte destruction) — the type mirrors that.
            destroy: async (id: string, body: { confirm: string; wipeDevices: boolean }): Promise<Task> =>
                unwrap(await transport.POST("/api/v6/hci/cephClusters/{cephCluster}/actions/destroy", { params: { path: { cephCluster: id } }, body: body as never }), zTask),
        },
    };



    const disks = {
        list: async (p: ListParams<FilterableFields["listDisks"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/inventory/disks", { params: { query: listQuery(p) as never } }), zDisk),

        listAll: (p: Omit<ListParams<FilterableFields["listDisks"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => disks.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/inventory/disks/{disk}", { params: { path: { disk: id } } }), zDisk),

        create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<WriteResult<Record<string, unknown>>> =>
            writeResult(await transport.POST("/api/v6/inventory/disks", { params: { query: writeQuery(opts) as never }, body: body as never }), zDisk),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/inventory/disks/{disk}", {
                params: { path: { disk: id }, query: writeQuery(opts) as never },
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zDisk),

        delete: async (id: string): Promise<Task> =>
            unwrap(await transport.DELETE("/api/v6/inventory/disks/{disk}", { params: { path: { disk: id } } }), zTask),

        batchDelete: async (ids: string[]): Promise<BatchDeleteResult> =>
            unwrap(await transport.DELETE("/api/v6/inventory/disks/batch", { body: { ids } as never }), zBatchDeleteResult),

        // Mobility round (2026-07-23, Annex E): move keeps the disk's id, clone
        // births a new disk (the task's target) — both answer 202 + Task.
        move: async (id: string, body: { storage: string }): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/inventory/disks/{disk}/actions/move", { params: { path: { disk: id } }, body: body as never }), zTask),

        clone: async (id: string, body: { name?: string; storage?: string; linked?: boolean } = {}): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/inventory/disks/{disk}/actions/clone", { params: { path: { disk: id } }, body: body as never }), zTask),

        // #107 (DESTRUCTIVE): the unused qcow2 becomes a vm-template catalog
        // file on the same storage and the disk ceases to exist — 202 + Task,
        // the new catalog file is the task's target.
        convertToTemplate: async (id: string, body: { catalog: string; name?: string }): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/inventory/disks/{disk}/actions/convertToTemplate", { params: { path: { disk: id } }, body: body as never }), zTask),
    };



    function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
        const headers: Record<string, string> = { ...extra };
        if (token !== null) {
            headers["authorization"] = `Bearer ${token}`;
        }
        return headers;
    }



    // The deliberate synchronous byte path (Annex G.1), two forms since the
    // chunked round (2026-07-20), shared by every content-bearing collection
    // (catalog files since round K1): a SIZED body (Blob/File/Uint8Array/
    // UploadSource) goes up in sequential Content-Range chunks — each request
    // small and fast, so the upload survives any proxy in between (the Next dev
    // rewrite kills large and long single PUTs — proven), reports progress per
    // chunk and resumes from the server's offset on a 409 resync. A
    // ReadableStream has no declarable total, so Content-Range cannot express
    // it: it falls back to the whole-body PUT — the LAST RESORT, not a peer.
    // The comment here used to call that "the CLI's direct, proxy-free path",
    // and the CLI believed it: a real ISO to our own demo host died on
    // nginx's `client_max_body_size` with a bare 413 (Luca, 2026-08-06). Every
    // caller that knows its length must take the chunked form.
    // The second half of that story is #257 (2026-08-09): the CLI's fix back
    // then was `openAsBlob`, and a Blob measured by Node LIES past 4 GiB
    // (`.size` modulo 2^32 — Luca's 4.3 GB ISO announced 231 MB and uploaded
    // "successfully"). Hence `UploadSource`, hence the two guards below —
    // every chunk must be exactly as long as declared (readUploadChunk), and a
    // server finalize that arrives before the last byte is an ERROR, not a
    // green light.
    async function uploadContentTo(
        url: string,
        content: ReadableStream<Uint8Array> | Uint8Array | Blob | UploadSource,
        opts: { chunkBytes?: number; onProgress?: (sentBytes: number, totalBytes: number) => void } = {},
    ): Promise<void> {
        if (content instanceof ReadableStream) {
            const response = await fetchImpl(url, {
                method: "PUT",
                headers: authHeaders({ "content-type": "application/octet-stream" }),
                body: content,
                duplex: "half",
            });
            if (!response.ok) {
                throw new TVirtApiError(response.status, (await response.json().catch(() => ({}))) as Record<string, unknown>);
            }
            return;
        }
        const source = uploadSourceOf(content);
        const total = source.sizeBytes;
        if (total === 0) {
            // A zero-byte body cannot be expressed as a range — whole-body PUT.
            const response = await fetchImpl(url, {
                method: "PUT",
                headers: authHeaders({ "content-type": "application/octet-stream" }),
                body: new Uint8Array(0),
            });
            if (!response.ok) {
                throw new TVirtApiError(response.status, (await response.json().catch(() => ({}))) as Record<string, unknown>);
            }
            opts.onProgress?.(0, 0);
            return;
        }
        // Default 8 MiB: measured 2026-07-20 — the Next dev rewrite proxy
        // rejects request bodies over 10 MiB (500), and it is the exact hop
        // this chunking exists to survive. 8 stays under it with margin;
        // callers on a direct connection may raise it.
        const chunkBytes = opts.chunkBytes ?? 8 * 1024 * 1024;
        let offset = 0;
        let resyncs = 0;
        for (;;) {
            const end = Math.min(offset + chunkBytes, total);
            const response = await fetchImpl(url, {
                method: "PUT",
                headers: authHeaders({
                    "content-type": "application/octet-stream",
                    "content-range": `bytes ${offset}-${Math.max(end - 1, 0)}/${total}`,
                }),
                body: await readUploadChunk(source, offset, end),
            });
            if (response.status === 409) {
                // Offset resync (crash-resume): the server names the bytes it holds.
                const expected = Number(response.headers.get("x-tvirt-upload-offset"));
                if (Number.isFinite(expected) && expected !== offset && resyncs++ < 3) {
                    await response.body?.cancel().catch(() => undefined);
                    offset = expected;
                    continue;
                }
                throw new TVirtApiError(409, (await response.json().catch(() => ({}))) as Record<string, unknown>);
            }
            if (!response.ok) {
                throw new TVirtApiError(response.status, (await response.json().catch(() => ({}))) as Record<string, unknown>);
            }
            offset = end;
            opts.onProgress?.(offset, total);
            if (response.status === 200) {
                if (offset < total) {
                    // #257: a finalize is a VERDICT on the whole object. One
                    // arriving with bytes still to send means client and server
                    // disagree on the length — never report that as success.
                    throw new TVirtApiError(500, {
                        title: "Upload finalized early",
                        detail: `the server finalized after ${offset} of ${total} bytes — the stored object is incomplete`,
                    });
                }
                return;
            }
            if (offset >= total) {
                // The server should have finalized on the last chunk (200).
                throw new TVirtApiError(500, { title: "Upload finalize missing", detail: "the final chunk did not finalize the upload" });
            }
        }
    }



    // Catalog files (round K1 — the inventory v2g merge): ONE collection for
    // every inert artefact — iso | ct-rootfs | vm-template | oci-image — living
    // inside exactly one catalog on one storage (the file's scope IS the
    // catalog's). Upload rides the shared chunked helper; move relocates the
    // OBJECT (202 + Task); copy is a synchronous new LINK (#93) — except on an
    // EMBEDDED source (#126), where it materializes bytes (202 + Task).
    const catalogFiles = {
        create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<WriteResult<Record<string, unknown>>> =>
            writeResult(await transport.POST("/api/v6/catalogFiles", { params: { query: writeQuery(opts) as never }, body: body as never }), zCatalogFile),

        uploadContent: async (
            id: string,
            content: ReadableStream<Uint8Array> | Uint8Array | Blob | UploadSource,
            opts: { chunkBytes?: number; onProgress?: (sentBytes: number, totalBytes: number) => void } = {},
        ): Promise<void> =>
            uploadContentTo(`${options.baseUrl}/api/v6/catalogFiles/${id}/content`, content, opts),

        // The bytes back, raw (412 while `phase` is not `ready`) — buffers
        // whole; prefer downloadContentStream for anything sizable, or
        // requestDownload for a browser navigation.
        downloadContent: async (id: string): Promise<Uint8Array> => {
            const response = await fetchImpl(`${options.baseUrl}/api/v6/catalogFiles/${id}/content`, {
                method: "GET",
                headers: authHeaders(),
            });
            if (!response.ok) {
                throw new TVirtApiError(response.status, (await response.json().catch(() => ({}))) as Record<string, unknown>);
            }
            return new Uint8Array(await response.arrayBuffer());
        },

        // The streaming form (round #108): the raw byte stream plus the
        // download facts — sizeBytes drives a progress bar, fileName rides
        // the server's Content-Disposition, `range` resumes ({ start } to
        // the end, or a closed { start, end } slice; the server answers 206).
        downloadContentStream: async (
            id: string,
            opts: { range?: { start: number; end?: number } } = {},
        ): Promise<{ status: number; body: ReadableStream<Uint8Array>; sizeBytes: number | null; fileName: string | null }> => {
            const headers = opts.range === undefined
                ? authHeaders()
                : authHeaders({ range: `bytes=${opts.range.start}-${opts.range.end ?? ""}` });
            const response = await fetchImpl(`${options.baseUrl}/api/v6/catalogFiles/${id}/content`, {
                method: "GET",
                headers,
            });
            if (!response.ok || response.body === null) {
                throw new TVirtApiError(response.status, (await response.json().catch(() => ({}))) as Record<string, unknown>);
            }
            const length = response.headers.get("content-length");
            const disposition = response.headers.get("content-disposition");
            return {
                status: response.status,
                body: response.body as ReadableStream<Uint8Array>,
                sizeBytes: length === null ? null : Number(length),
                fileName: disposition === null ? null : (/filename="([^"]+)"/.exec(disposition)?.[1] ?? null),
            };
        },

        // The browser mint (round #108): a ONE-SHOT relative URL to navigate
        // to — no Authorization header on that GET, the ticket in the URL is
        // the whole credential (the console-viewer idiom); it expires unused
        // in about a minute.
        requestDownload: async (id: string): Promise<{ downloadUrl: string; fileName: string }> =>
            unwrap(await transport.POST("/api/v6/catalogFiles/{file}/actions/download", {
                params: { path: { file: id } },
            }), zRequestCatalogFileDownloadResponse),

        list: async (p: ListParams<FilterableFields["listCatalogFiles"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/catalogFiles", { params: { query: listQuery(p) as never } }), zCatalogFile),

        listAll: (p: Omit<ListParams<FilterableFields["listCatalogFiles"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => catalogFiles.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/catalogFiles/{file}", { params: { path: { file: id } } }), zCatalogFile),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/catalogFiles/{file}", {
                params: { path: { file: id }, query: writeQuery(opts) as never },
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zCatalogFile),

        // #93 two-faced delete: 202 + Task when the LAST link goes (the bytes
        // die with it), 204 when only this link is removed — undefined then.
        delete: async (id: string): Promise<Task | undefined> => {
            const result = await transport.DELETE("/api/v6/catalogFiles/{file}", { params: { path: { file: id } } });
            if (result.response.status === 204) {
                unwrap(result);
                return undefined;
            }
            return unwrap(result, zTask);
        },

        batchDelete: async (ids: string[]): Promise<BatchDeleteResult> =>
            unwrap(await transport.DELETE("/api/v6/catalogFiles/batch", { body: { ids } as never }), zBatchDeleteResult),

        move: async (id: string, body: { storage: string }): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/catalogFiles/{file}/actions/move", { params: { path: { file: id } }, body: body as never }), zTask),

        // Round catmove: the CATALOG dimension of "move" — the LINK itself
        // re-homes (same id, same object, scope = the target catalog's), no
        // byte moves: synchronous, 200 + the updated file. `move` above stays
        // the storage dimension (a task).
        rehome: async (id: string, body: { catalog: string }): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/catalogFiles/{file}/actions/rehome", { params: { path: { file: id } }, body: body as never }), zCatalogFile),

        // ALWAYS 202 + Task (#147); the new file is the task's target. #93
        // still rules what a copy is: a NEW LINK to the same object, born
        // ready before the 202 answers (the task closes immediately). The
        // #126 embedded source materializes bytes instead (storage + catalog
        // required) and the task does real work.
        copy: async (id: string, body: { name?: string; storage?: string; catalog?: string } = {}): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/catalogFiles/{file}/actions/copy", { params: { path: { file: id } }, body: body as never }), zTask),

        // Round K3 (#64): materialize a vm-template file (tvtpl package or
        // naked qcow2) into a config-side VM Template — 202 + Task, the
        // newborn template is the task's target.
        importTemplate: async (id: string, body: { storage: string; name?: string; scope?: string; metadataStorage?: string }): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/catalogFiles/{file}/actions/importTemplate", { params: { path: { file: id } }, body: body as never }), zTask),

        // The ticket bridge (#108, in-contract since #148): streams the URL
        // minted by requestDownload — NO Authorization header, the tvdl_
        // ticket is the whole credential and dies as the stream starts.
        downloadByTicket: async (
            id: string,
            ticket: string,
        ): Promise<{ status: number; body: ReadableStream<Uint8Array>; sizeBytes: number | null; fileName: string | null }> => {
            const response = await fetchImpl(`${options.baseUrl}/api/v6/catalogFiles/${id}/download/${ticket}`, {
                method: "GET",
            });
            if (!response.ok || response.body === null) {
                throw new TVirtApiError(response.status, (await response.json().catch(() => ({}))) as Record<string, unknown>);
            }
            const length = response.headers.get("content-length");
            const disposition = response.headers.get("content-disposition");
            return {
                status: response.status,
                body: response.body as ReadableStream<Uint8Array>,
                sizeBytes: length === null ? null : Number(length),
                fileName: disposition === null ? null : (/filename="([^"]+)"/.exec(disposition)?.[1] ?? null),
            };
        },
    };



    // CTs (round K2 — the materialized container objects): a CT Template is a
    // CT with the `template` flag raised. The create IS the "import from
    // catalog" (201 + X-TVirt-Task, materializing → ready); exportToCatalog is
    // the inverse (202 + Task, the new catalog file is the task's target);
    // delete answers 202 + Task like the catalog files.
    const cts = {
        create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<WriteResult<Record<string, unknown>>> =>
            writeResult(await transport.POST("/api/v6/cts", { params: { query: writeQuery(opts) as never }, body: body as never }), zCt),

        list: async (p: ListParams<FilterableFields["listCts"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/cts", { params: { query: listQuery(p) as never } }), zCt),

        listAll: (p: Omit<ListParams<FilterableFields["listCts"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => cts.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/cts/{ct}", { params: { path: { ct: id } } }), zCt),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/cts/{ct}", {
                params: { path: { ct: id }, query: writeQuery(opts) as never },
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zCt),

        delete: async (id: string): Promise<Task> =>
            unwrap(await transport.DELETE("/api/v6/cts/{ct}", { params: { path: { ct: id } } }), zTask),

        batchDelete: async (ids: string[]): Promise<BatchDeleteResult> =>
            unwrap(await transport.DELETE("/api/v6/cts/batch", { body: { ids } as never }), zBatchDeleteResult),

        exportToCatalog: async (id: string, body: { catalog: string; name?: string; storage?: string }): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/cts/{ct}/actions/exportToCatalog", { params: { path: { ct: id } }, body: body as never }), zTask),

        // The runtime actions (round #61 — the LXC runtime, first support
        // unprivileged): 202 + Task each; stop's graceful mode falls back to
        // a kill after timeoutSeconds, hard preempts a hanging graceful.
        start: async (id: string, body: { node?: NodeRef } = {}): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/cts/{ct}/actions/start", { params: { path: { ct: id } }, body: body as never }), zTask),

        stop: async (id: string, body: { mode?: "graceful" | "hard"; timeoutSeconds?: number } = {}): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/cts/{ct}/actions/stop", { params: { path: { ct: id } }, body: body as never }), zTask),

        restart: async (id: string): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/cts/{ct}/actions/restart", { params: { path: { ct: id } } }), zTask),

        // The template transitions (round #97 — the VM markTemplate idiom,
        // minus the freeze): synchronous config, 200 with the refreshed CT.
        markAsTemplate: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/cts/{ct}/actions/markAsTemplate", { params: { path: { ct: id } } }), zCt),

        markAsCt: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/cts/{ct}/actions/markAsCt", { params: { path: { ct: id } } }), zCt),

        // Console and logs (round #347) — the two ways to look inside a CT.
        //
        // `console` mints the ticket and hands back the WebSocket URL, the
        // `vms.view` shape: authorization happens HERE, and the returned URL
        // carries the whole credential (a raw WebSocket cannot hold a bearer
        // header). There is deliberately no wrapper for the bridge itself —
        // it is a WebSocket, not a REST call, exactly as on the VM side.
        // `geometry` (round #356) is optional and says how big the viewer is
        // ABOUT to be: a terminal emulator knows its size before it opens the
        // socket, and saying it here is what keeps the container's console
        // from being born at 24x80 and corrected a frame later. Resizes after
        // that ride the bridge's own control channel, not this call.
        console: async (id: string, geometry?: { rows: number; cols: number }): Promise<{ consoleWebSocketUrl: string; proto: string }> =>
            unwrap(await transport.POST("/api/v6/cts/{ct}/actions/console", {
                params: { path: { ct: id }, query: { proto: "tty", ...geometry } as never },
            }), zCtConsoleAccess),

        // Why a dead console socket died — a takeover told from a blip.
        // Ticket-authenticated and always 200 (`unknown` on a miss). Since
        // T157-P8 d it also carries the console ENDPOINT's observed state
        // (`endpoint`): the worker's own words when the socket could not be
        // opened, so a viewer that met a 1011 reads the cause instead of
        // retrying forever.
        consoleFate: async (id: string, ticket: string): Promise<z.infer<typeof zCtConsoleFate>> =>
            unwrap(await transport.GET("/api/v6/cts/{ct}/console/{ticket}/fate", {
                params: { path: { ct: id, ticket } },
            }), zCtConsoleFate),

        // The exec session of a CT (T157-P8 f-ii) — `apps.exec` one runtime
        // over, on `lxc-attach`: the mint is the only authenticated call, the
        // ticket in the URL is the WebSocket's whole credential. `mode: "pipe"`
        // needs `argv`; `mode: "tty"` defaults to /bin/sh and takes the
        // terminal's geometry so the pty is born the right size. No
        // `container`: a CT is one.
        exec: async (id: string, body: { mode: "tty" | "pipe"; argv?: string[]; rows?: number; cols?: number }): Promise<{ execWebSocketUrl: string; mode: "tty" | "pipe" }> =>
            unwrap(await transport.POST("/api/v6/cts/{ct}/actions/exec", {
                params: { path: { ct: id } },
                body: body as never,
            }), zCtExecAccess),

        // `logs` enqueues the capture task whose FRAMES carry the lines
        // (tasks.log reads them) — the `apps.logs` idiom. `source` chooses
        // between the container's own init console and liblxc's supervisor
        // log: two different questions, and the caller knows which it is
        // asking.
        logs: async (id: string, body: { tail?: number; source?: "console" | "supervisor" } = {}): Promise<Task> =>
            unwrap(await transport.POST("/api/v6/cts/{ct}/actions/logs", {
                params: { path: { ct: id } },
                body: body as never,
            }), zTask),
    };



    // Annex O (revised by fabric-ipam-revision-notes.md): the Fabric is the
    // physical registry — one flat document (logical networks +
    // switches/ports/laggs), pure config: create answers 201 with no task,
    // PATCH replaces the arrays whole; scope is fixed /cluster1 and every
    // logical-network subnet derives its IPAM row in the same write.
    const fabrics = {
        list: async (p: ListParams<FilterableFields["listFabrics"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/network/fabrics", { params: { query: listQuery(p) as never } }), zFabric),

        listAll: (p: Omit<ListParams<FilterableFields["listFabrics"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => fabrics.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/network/fabrics/{fabric}", { params: { path: { fabric: id } } }), zFabric),

        create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<WriteResult<Record<string, unknown>>> =>
            writeResult(await transport.POST("/api/v6/network/fabrics", { params: { query: writeQuery(opts) as never }, body: body as never }), zFabric),

        // R6 «Identità e revisioni» (ratified 2026-08-06): a patch NAMING
        // `logicalNetworks`/`switches`/`laggs` REPLACES those arrays whole, so
        // the server demands the precondition — pass the `etag` read with the
        // document (it is a field of the fabric, so a list already carries it)
        // and the write refuses `412` instead of deleting another operator's
        // logical network in silence. `"*"` is the deliberate overwrite. A
        // patch of `notes` alone needs nothing.
        patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/network/fabrics/{fabric}", {
                params: { path: { fabric: id }, query: writeQuery(opts) as never },
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zFabric),

        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v6/network/fabrics/{fabric}", { params: { path: { fabric: id } } }));
        },

        batchDelete: async (ids: string[]): Promise<BatchDeleteResult> =>
            unwrap(await transport.DELETE("/api/v6/network/fabrics/batch", { body: { ids } as never }), zBatchDeleteResult),

        // Round FABRIC F6: the derivation preview/apply pair (design #137, H1
        // explicit apply). The preview is what-if PURE and carries the anchors
        // the apply must send back — `fabricEtags` (the COMPOSITE P10 anchor
        // over the R6 validators, round #405 §5.3: this fabric, the owners of
        // its imported networks, the other fabrics the nodes are cabled to —
        // one that moved answers 409 naming it) and the per-node document
        // `generation` (a node that moved refuses item-level,
        // `documentMoved`). The apply is the sister door of the host-network
        // editor: host.network + sudo + cluster authority.
        nodeConfigs: async (id: string): Promise<FabricNodeConfigPlan> =>
            unwrap(await transport.GET("/api/v6/network/fabrics/{fabric}/node-configs", { params: { path: { fabric: id } } }), zFabricNodeConfigPlan),

        applyNodeConfigs: async (id: string, body: FabricNodeConfigApply): Promise<FabricNodeConfigApplyResult> =>
            unwrap(await transport.POST("/api/v6/network/fabrics/{fabric}/node-configs/apply", { params: { path: { fabric: id } }, body: body as never }), zFabricNodeConfigApplyResult),

        // RP-P1: the H13 undo (F1-P3) — `fabrics[<id>].previous` rewritten as
        // a NEW generation, anchored to `expectedGeneration` (409 speaking
        // when the document moved, nothing written; 409 too when another
        // fabric applied on the node AFTER this one — LIFO, SN-P12). Same
        // triple as the apply:
        // host.network + sudo + cluster authority. The plan's
        // `previousGeneration` says whether there is anything to restore.
        restoreNodeConfig: async (id: string, body: FabricNodeConfigRestore): Promise<FabricNodeConfigRestoreResult> =>
            unwrap(await transport.POST("/api/v6/network/fabrics/{fabric}/node-configs/restore", { params: { path: { fabric: id } }, body: body as never }), zFabricNodeConfigRestoreResult),

        // Round #405 (SN-P1-bis / SN-P9): the adhesion pair. `importNetwork`
        // takes `<owner fabric>/<network>` (fabric = id or /scope/name) and is
        // IDEMPOTENT — 201 on the first import, 200 with the same entry on a
        // repeat; the importer's etag moves (its resolved view changed), the
        // owner's does not. `unimportNetwork` takes the BARE name as known in
        // the importer's resolved view and refuses 409 speaking while a
        // port/LAGG of the importer still maps it, or a guest/App/listener is
        // served only through this leg (SN-P13).
        importNetwork: async (id: string, network: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/network/fabrics/{fabric}/imports", { params: { path: { fabric: id } }, body: { network } as never }), zFabricImportedNetwork),

        unimportNetwork: async (id: string, network: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v6/network/fabrics/{fabric}/imports/{network}", { params: { path: { fabric: id, network } } }));
        },
    };



    // The derived IPAM (fabric-ipam-revision-notes.md, 2026-07-28): subnets
    // are READ-ONLY rows derived from fabric logical networks and overlays —
    // the write surface died with the revision; the view endpoint renders
    // every derived subnet with provenance-labeled addresses.
    const subnets = {
        list: async (p: ListParams<FilterableFields["listSubnets"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/network/subnets", { params: { query: listQuery(p) as never } }), zSubnet),

        listAll: (p: Omit<ListParams<FilterableFields["listSubnets"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => subnets.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/network/subnets/{subnet}", { params: { path: { subnet: id } } }), zSubnet),
    };



    // Annex P (ratified 2026-07-26): managed network services — one
    // collection, kind discriminator, task-less convergence (§9.2).
    const networkServices = {
        list: async (p: ListParams<FilterableFields["listNetworkServices"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/network/services", { params: { query: listQuery(p) as never } }), zNetworkService),

        listAll: (p: Omit<ListParams<FilterableFields["listNetworkServices"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => networkServices.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/network/services/{service}", { params: { path: { service: id } } }), zNetworkService),

        create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<WriteResult<Record<string, unknown>>> =>
            writeResult(await transport.POST("/api/v6/network/services", { params: { query: writeQuery(opts) as never }, body: body as never }), zNetworkService),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/network/services/{service}", {
                params: { path: { service: id }, query: writeQuery(opts) as never },
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zNetworkService),

        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v6/network/services/{service}", { params: { path: { service: id } } }));
        },

        batchDelete: async (ids: string[]): Promise<BatchDeleteResult> =>
            unwrap(await transport.DELETE("/api/v6/network/services/batch", { body: { ids } as never }), zBatchDeleteResult),

        // Round L — the transferVmAuthority twin: moves the durable authority
        // assignment; the target must be in the placement census's candidate
        // set (422 otherwise), and the workers' next pass is the act.
        transferAuthority: async (id: string, node: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/network/services/{service}/actions/transferAuthority", {
                params: { path: { service: id } },
                body: { node } as never,
            }), zNetworkService),

        // #343 — where this instance may live. `exclude` simulates members
        // gone: it is the drain what-if (#133) for network services, asked
        // BEFORE the evacuation instead of discovered at failover.
        placement: async (id: string, exclude: string[] = []): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/network/services/{service}/placement", {
                params: { path: { service: id }, query: { exclude } as never },
            }), zNetsvcPlacement),

        // The same census over the whole visible fleet, ONE registry read: the
        // drain dialog's question ("which services would have nowhere left to
        // live?") and the fragility list in one answer.
        fleetPlacement: async (exclude: string[] = [], scope?: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/network/services/placement", {
                params: { query: { exclude, ...(scope !== undefined ? { scope } : {}) } as never },
            }), zNetsvcPlacementList),

        // The vpn kind's profile export (ratified 2026-08-02): the mint
        // returns a ONE-SHOT relative URL — the ticket in it is the whole
        // credential (the catalog download idiom), so the GET below carries
        // no Authorization header and spends the ticket as it answers.
        requestVpnProfile: async (id: string, peer: string): Promise<{ downloadUrl: string; fileName: string }> =>
            unwrap(await transport.POST("/api/v6/network/services/{service}/actions/exportProfile", {
                params: { path: { service: id } },
                body: { peer } as never,
            }), zExportVpnProfileResponse),

        // The bridge, followed for callers that want the bytes rather than a
        // link to hand a browser (the CLI's export command).
        downloadVpnProfile: async (id: string, peer: string): Promise<{ fileName: string; content: string }> => {
            const minted = await networkServices.requestVpnProfile(id, peer);
            const response = await fetchImpl(`${options.baseUrl}${minted.downloadUrl}`, { method: "GET" });
            if (!response.ok) {
                throw new TVirtApiError(response.status, (await response.json().catch(() => ({}))) as Record<string, unknown>);
            }
            return { fileName: minted.fileName, content: await response.text() };
        },
    };



    const ipam = {
        view: async (): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/network/ipam", {}), zIpamView),
    };



    // R4 (host-network-design.md §9): overlays replace virtual networks.
    // Reconciler-driven lifecycle — create returns the plain object (no task),
    // delete answers 204 (void).
    const overlayNetworks = {
        list: async (p: ListParams<FilterableFields["listOverlayNetworks"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/overlayNetworks", { params: { query: listQuery(p) as never } }), zOverlayNetwork),

        listAll: (p: Omit<ListParams<FilterableFields["listOverlayNetworks"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => overlayNetworks.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/overlayNetworks/{network}", { params: { path: { network: id } } }), zOverlayNetwork),

        create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<WriteResult<Record<string, unknown>>> =>
            writeResult(await transport.POST("/api/v6/overlayNetworks", { params: { query: writeQuery(opts) as never }, body: body as never }), zOverlayNetwork),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/overlayNetworks/{network}", {
                params: { path: { network: id }, query: writeQuery(opts) as never },
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zOverlayNetwork),

        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v6/overlayNetworks/{network}", { params: { path: { network: id } } }));
        },

        batchDelete: async (ids: string[]): Promise<BatchDeleteResult> =>
            unwrap(await transport.DELETE("/api/v6/overlayNetworks/batch", { body: { ids } as never }), zBatchDeleteResult),
    };



    const users = {
        list: async (p: ListParams<FilterableFields["listUsers"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/users", { params: { query: listQuery(p) as never } }), zUser),

        listAll: (p: Omit<ListParams<FilterableFields["listUsers"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => users.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/users/{user}", { params: { path: { user: id } } }), zUser),

        // R6f2-P3 (ratified 2026-08-27): the self-service pair — the PATCH
        // method the fase-2 census left named, plus the read half its
        // validator rides on (one validator for the two faces of User).
        getMyself: async (): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/users/myself"), zUser),

        patchMyself: async (mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/users/myself", {
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zUser),

        create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/users", { params: { query: writeQuery(opts) as never }, body: body as never }), zUser),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/users/{user}", {
                params: { path: { user: id }, query: writeQuery(opts) as never },
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zUser),

        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v6/users/{user}", { params: { path: { user: id } } }));
        },

        batchDelete: async (ids: string[]): Promise<BatchDeleteResult> =>
            unwrap(await transport.DELETE("/api/v6/users/batch", { body: { ids } as never }), zBatchDeleteResult),

        // Administrative replace; `mustChangePassword` (contract default true)
        // rides when the caller says so — parity R1, the census's S4 entry.
        setPassword: async (id: string, newPassword: string, opts: { mustChangePassword?: boolean } = {}): Promise<void> => {
            unwrap(await transport.PUT("/api/v6/users/{user}/password", {
                params: { path: { user: id } },
                body: { newPassword, ...(opts.mustChangePassword !== undefined ? { mustChangePassword: opts.mustChangePassword } : {}) } as never,
            }));
        },

        // Self-service change (clears mustChangePassword): verifies currentPassword,
        // 403 on mismatch, 422 against the password policy. Existing sessions survive.
        changeMyPassword: async (currentPassword: string, newPassword: string): Promise<void> => {
            unwrap(await transport.PUT("/api/v6/users/myself/password", {
                body: { currentPassword, newPassword } as never,
            }));
        },

        // Clears the Annex B lockout thresholds.
        unlock: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/users/{user}/actions/unlock", {
                params: { path: { user: id } },
            }), zUser),

        // Kills the user's sessions; includeTokens also revokes the app tokens.
        revokeSessions: async (id: string, opts: { includeTokens?: boolean } = {}): Promise<void> => {
            unwrap(await transport.POST("/api/v6/users/{user}/actions/revokeSessions", {
                params: { path: { user: id } },
                body: opts as never,
            }));
        },
    };



    // User groups (round #103): scoped, FLAT sets of users. Membership writes go
    // through addMember/removeMember — the members field is read-only.
    const groups = {
        list: async (p: ListParams<FilterableFields["listGroups"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/groups", { params: { query: listQuery(p) as never } }), zGroup),

        listAll: (p: Omit<ListParams<FilterableFields["listGroups"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => groups.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/groups/{group}", { params: { path: { group: id } } }), zGroup),

        create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/groups", { params: { query: writeQuery(opts) as never }, body: body as never }), zGroup),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/groups/{group}", {
                params: { path: { group: id }, query: writeQuery(opts) as never },
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zGroup),

        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v6/groups/{group}", { params: { path: { group: id } } }));
        },

        batchDelete: async (ids: string[]): Promise<BatchDeleteResult> =>
            unwrap(await transport.DELETE("/api/v6/groups/batch", { body: { ids } as never }), zBatchDeleteResult),

        // `user` is the user id or the bare username; idempotent (re-adding a
        // member answers 200 with the unchanged group).
        addMember: async (id: string, user: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/groups/{group}/members", {
                params: { path: { group: id } },
                body: { user } as never,
            }), zGroup),

        removeMember: async (id: string, user: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v6/groups/{group}/members/{member}", {
                params: { path: { group: id, member: user } },
            }));
        },
    };



    const scopes = {
        list: async (p: ListParams<FilterableFields["listScopes"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/scopes", { params: { query: listQuery(p) as never } }), zScope),

        listAll: (p: Omit<ListParams<FilterableFields["listScopes"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => scopes.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/scopes/{scope}", { params: { path: { scope: id } } }), zScope),

        create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/scopes", { params: { query: writeQuery(opts) as never }, body: body as never }), zScope),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/scopes/{scope}", {
                params: { path: { scope: id }, query: writeQuery(opts) as never },
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zScope),

        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v6/scopes/{scope}", { params: { path: { scope: id } } }));
        },

        batchDelete: async (ids: string[]): Promise<BatchDeleteResult> =>
            unwrap(await transport.DELETE("/api/v6/scopes/batch", { body: { ids } as never }), zBatchDeleteResult),
    };



    const rbac = {
        permissions: async (): Promise<Record<string, unknown>[]> => {
            const body = unwrap<{ items: Record<string, unknown>[] }>(
                await transport.GET("/api/v6/rbac/permissions"),
                zPermissionList,
            );
            return body.items;
        },

        roles: {
            list: async (p: ListParams<FilterableFields["listRbacRoles"]> = {}): Promise<Page<Record<string, unknown>>> =>
                page(await transport.GET("/api/v6/rbac/roles", { params: { query: listQuery(p) as never } }), zRole),

            listAll: (p: Omit<ListParams<FilterableFields["listRbacRoles"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
                genPageIterator(async ({ cursor }) => rbac.roles.list({ ...p, cursor })),

            get: async (id: string): Promise<Record<string, unknown>> =>
                unwrap(await transport.GET("/api/v6/rbac/roles/{role}", { params: { path: { role: id } } }), zRole),

            create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<Record<string, unknown>> =>
                unwrap(await transport.POST("/api/v6/rbac/roles", { params: { query: writeQuery(opts) as never }, body: body as never }), zRole),

            patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
                unwrap(await transport.PATCH("/api/v6/rbac/roles/{role}", {
                    params: { path: { role: id }, query: writeQuery(opts) as never },
                    body: mergePatch as never,
                    headers: mergePatchHeaders(opts),
                }), zRole),

            delete: async (id: string): Promise<void> => {
                unwrap(await transport.DELETE("/api/v6/rbac/roles/{role}", { params: { path: { role: id } } }));
            },

            batchDelete: async (ids: string[]): Promise<BatchDeleteResult> =>
                unwrap(await transport.DELETE("/api/v6/rbac/roles/batch", { body: { ids } as never }), zBatchDeleteResult),
        },

        grants: {
            list: async (p: ListParams<FilterableFields["listRbacGrants"]> = {}): Promise<Page<Record<string, unknown>>> =>
                page(await transport.GET("/api/v6/rbac/grants", { params: { query: listQuery(p) as never } }), zGrant),

            listAll: (p: Omit<ListParams<FilterableFields["listRbacGrants"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
                genPageIterator(async ({ cursor }) => rbac.grants.list({ ...p, cursor })),

            // Parity R1: the one facade the census found missing (getRbacGrant).
            get: async (id: string): Promise<Record<string, unknown>> =>
                unwrap(await transport.GET("/api/v6/rbac/grants/{grant}", { params: { path: { grant: id } } }), zGrant),

            create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<Record<string, unknown>> =>
                unwrap(await transport.POST("/api/v6/rbac/grants", { params: { query: writeQuery(opts) as never }, body: body as never }), zGrant),

            delete: async (id: string): Promise<void> => {
                unwrap(await transport.DELETE("/api/v6/rbac/grants/{grant}", { params: { path: { grant: id } } }));
            },

            batchDelete: async (ids: string[]): Promise<BatchDeleteResult> =>
                unwrap(await transport.DELETE("/api/v6/rbac/grants/batch", { body: { ids } as never }), zBatchDeleteResult),
        },
    };



    const cluster = {
        // Two faces (#157 censimento singleton): the full Cluster for
        // cluster-root authority, the reduced ClusterStatus (name + phase)
        // for any other cluster.read holder — distinguish by `quorum`.
        get: async (): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/cluster"), zGetClusterResponse),

        patch: async (mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/cluster", {
                params: { query: writeQuery(opts) as never },
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zCluster),

        // The inter-node connectivity matrix (#326, GET /cluster/links): one
        // row per ordered pair per role, `rtt` probed each supervision pass.
        // Read-only; round hw-prep (2026-09-09) — the fact the hw-suite runner
        // (S0/S1) and `tvirt diagnose --area hardware` read. No CLI verb and no
        // GUI view yet (HW-P18, R2): the facade is the declared debt's floor.
        // `404` below cluster-root authority (fleet telemetry, #157).
        links: async (): Promise<LinkMatrix> =>
            unwrap(await transport.GET("/api/v6/cluster/links"), zLinkMatrix),

        nodes: {
            list: async (p: ListParams<FilterableFields["listClusterNodes"]> = {}): Promise<Page<Record<string, unknown>>> =>
                page(await transport.GET("/api/v6/cluster/nodes", { params: { query: listQuery(p) as never } }), zClusterNode),

            listAll: (p: Omit<ListParams<FilterableFields["listClusterNodes"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
                genPageIterator(async ({ cursor }) => cluster.nodes.list({ ...p, cursor })),

            // The joinToken rides only this response — print or store it immediately.
            add: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<Record<string, unknown>> =>
                unwrap(await transport.POST("/api/v6/cluster/nodes", { params: { query: writeQuery(opts) as never }, body: body as never }), zAddClusterNodeResponse),

            delete: async (id: string): Promise<void> => {
                unwrap(await transport.DELETE("/api/v6/cluster/nodes/{node}", { params: { path: { node: id } } }));
            },

            // The node's writable surface (settings refactor, ratified 2026-07-24):
            // labels, notes and the per-node `settings` block. Config write — 503
            // while the store is down.
            patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
                unwrap(await transport.PATCH("/api/v6/cluster/nodes/{node}", {
                    params: { path: { node: id }, query: writeQuery(opts) as never },
                    body: mergePatch as never,
                    headers: mergePatchHeaders(opts),
                }), zClusterNode),

            // Store-member management (2026-07-14): promote/demote run parent tasks,
            // restart bounces the services. All return the X-TVirt-Task ref.
            promote: async (id: string): Promise<WriteResult<Record<string, unknown>>> =>
                writeResult(await transport.POST("/api/v6/cluster/nodes/{node}/actions/promote", { params: { path: { node: id } } })),

            demote: async (id: string): Promise<WriteResult<Record<string, unknown>>> =>
                writeResult(await transport.POST("/api/v6/cluster/nodes/{node}/actions/demote", { params: { path: { node: id } } })),

            restartStore: async (id: string): Promise<WriteResult<Record<string, unknown>>> =>
                writeResult(await transport.POST("/api/v6/cluster/nodes/{node}/actions/restartStore", { params: { path: { node: id } } })),

            // Host-network single-operation editor (host-network-design.md §2, ratified
            // 2026-07-20): each PATCH applies ONE op to the node's desired document,
            // bumps its dedicated network generation, and answers the refreshed node
            // render. `host.network` permission + sudo.
            editNetwork: async (id: string, op: Record<string, unknown>): Promise<Record<string, unknown>> =>
                unwrap(await transport.PATCH("/api/v6/cluster/nodes/{node}/network", {
                    params: { path: { node: id } },
                    body: op as never,
                }), zClusterNode),

            // Operator attestation (cluster-first §8, Fase 5): one act — the node's
            // tasks fail, leases release, authorities become transferable. Sudo.
            attestDown: async (id: string): Promise<void> => {
                unwrap(await transport.POST("/api/v6/cluster/nodes/{node}/actions/attestDown", { params: { path: { node: id } } }));
            },

            // #233 — the read-only service access of a node (T233-P2, R4b). The
            // grant answers 202 with the generated password EXACTLY ONCE (keep it
            // in memory, never re-fetchable — the joinToken precedent) and the
            // task that sets it on the node; the revoke answers the updated record
            // plus the re-lock task ref. Both `cluster.node.readOnlyAccess` + sudo.
            grantReadOnlyAccess: async (id: string, body: { reason: string; ttlSeconds?: number }): Promise<ReadOnlyAccessGranted> =>
                unwrap(await transport.POST("/api/v6/cluster/nodes/{node}/actions/grantReadOnlyAccess", {
                    params: { path: { node: id } },
                    body: body as never,
                }), zReadOnlyAccessGranted),

            revokeReadOnlyAccess: async (id: string): Promise<WriteResult<ReadOnlyAccess>> =>
                writeResult(await transport.POST("/api/v6/cluster/nodes/{node}/actions/revokeReadOnlyAccess", { params: { path: { node: id } } }), zReadOnlyAccess),

            // Node maintenance (§8.1, ratified 2026-07-23): enter cordons the node
            // and runs the async node.drain parent task (202 + X-TVirt-Task); exit
            // clears the flag and answers the refreshed node render. Both sudo.
            enterMaintenance: async (id: string): Promise<WriteResult<Record<string, unknown>>> =>
                writeResult(await transport.POST("/api/v6/cluster/nodes/{node}/actions/enterMaintenance", { params: { path: { node: id } } })),

            exitMaintenance: async (id: string): Promise<Record<string, unknown>> =>
                unwrap(await transport.POST("/api/v6/cluster/nodes/{node}/actions/exitMaintenance", { params: { path: { node: id } } }), zClusterNode),

            // Round #133a what-if drain: the read-only evacuation plan — per live
            // VM on the node, the predicted outcome (migrate/migrateWarning/stop)
            // with the target and the structured reasons. Purely observational.
            evacuationPlan: async (id: string): Promise<NodeEvacuationPlan> =>
                unwrap(await transport.GET("/api/v6/cluster/nodes/{node}/evacuationPlan", { params: { path: { node: id } } }), zNodeEvacuationPlan),

            // #166 (T166-P6): the node-firewall lens — posture rendered
            // against, derived registry of platform ports, component state
            // (phase, generation, deadline, last rollback), conditions. Id or
            // registry name.
            firewall: async (id: string): Promise<Record<string, unknown>> =>
                unwrap(await transport.GET("/api/v6/cluster/nodes/{node}/firewall", { params: { path: { node: id } } }), zNodeFirewall),

            // Node historical series (node-stats round, 2026-08-01 — Annex I.2):
            // the exact StatsSeries shape of vms.stats; v1 catalog cpu.usage +
            // mem.used.
            // #111: the series now come from the STORE, so a REMOTE node answers
            // here too; the window may be relative (`range`) or absolute
            // (`from`/`to`), `agg` picks weighted mean vs per-bucket peak, and
            // the answer declares the TIER and WHERE it was served from.
            stats: async (id: string, opts: { metric: string[]; range?: string; from?: string; to?: string; step?: number; agg?: "avg" | "max" }): Promise<{ step: number; resolution: number | null; servedFrom: "store" | "node"; series: Record<string, unknown>[] }> =>
                unwrap(await transport.GET("/api/v6/cluster/nodes/{node}/stats", {
                    params: {
                        path: { node: id },
                        query: {
                            metric: opts.metric.join(","),
                            ...(opts.range !== undefined ? { range: opts.range } : {}),
                            ...(opts.from !== undefined ? { from: opts.from } : {}),
                            ...(opts.to !== undefined ? { to: opts.to } : {}),
                            ...(opts.step !== undefined ? { step: opts.step } : {}),
                            ...(opts.agg !== undefined ? { agg: opts.agg } : {}),
                        } as never,
                    },
                }), zStatsSeries),
        },

        // Parity R1 (2026-11-03): the superseded runtime create/join/delete
        // facades are gone with their contract operations — clusters are
        // install-born (db:init-cluster / db:init-join in dev).
    };



    // TVirt Proximity (round #108, §8.1 v2): the config+observed panel and the
    // affinity/anti-affinity rules. The panel needs the store (503 when down);
    // rules are ordinary replicated config resources.
    const proximity = {
        get: async (): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/cluster/proximity"), zProximityStatus),

        patch: async (mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/cluster/proximity", {
                params: { query: writeQuery(opts) as never },
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zProximityStatus),

        rules: {
            list: async (p: ListParams<FilterableFields["listProximityRules"]> = {}): Promise<Page<Record<string, unknown>>> =>
                page(await transport.GET("/api/v6/proximityRules", { params: { query: listQuery(p) as never } }), zProximityRule),

            listAll: (p: Omit<ListParams<FilterableFields["listProximityRules"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
                genPageIterator(async ({ cursor }) => proximity.rules.list({ ...p, cursor })),

            get: async (id: string): Promise<Record<string, unknown>> =>
                unwrap(await transport.GET("/api/v6/proximityRules/{rule}", { params: { path: { rule: id } } }), zProximityRule),

            create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<Record<string, unknown>> =>
                unwrap(await transport.POST("/api/v6/proximityRules", { params: { query: writeQuery(opts) as never }, body: body as never }), zProximityRule),

            patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
                unwrap(await transport.PATCH("/api/v6/proximityRules/{rule}", {
                    params: { path: { rule: id }, query: writeQuery(opts) as never },
                    body: mergePatch as never,
                    headers: mergePatchHeaders(opts),
                }), zProximityRule),

            delete: async (id: string): Promise<void> => {
                unwrap(await transport.DELETE("/api/v6/proximityRules/{rule}", { params: { path: { rule: id } } }));
            },
        },
    };



    // The Home dashboard's aggregate (ratified 2026-07-28): one call, zones
    // absent where the caller has no visibility; 503 while the store is down.
    const system = {
        overview: async (): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/system/overview"), zSystemOverview),

        // The Esplora census aggregate (task #151): scopes, catalogs and the
        // flat object census in one call — per-family strict visibility
        // server-side; 503 while the store is down.
        explorer: async (): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/system/explorer"), zSystemExplorer),

        // The diagnose run (#392): the ONE implementation of the API-based
        // areas, consumed identically by `tvirt diagnose` and by Sistema >
        // Diagnostica — neither surface computes verdicts of its own.
        diagnose: async (p: { area?: string[]; sinceHours?: number } = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/system/diagnose", { params: { query: p as never } }), zGetSystemDiagnoseResponse),

        // The diagnostic-bundle mint (#385): a one-shot browser URL, the
        // catalog-download idiom — the ticket in the URL is the credential.
        requestDiagnoseBundle: async (): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/system/diagnose/actions/export-bundle"), zRequestDiagnoseBundleResponse),
    };



    // The cluster's subscription license (round Licensing, 2026-07-29): a
    // singleton — GET is the license with computed usage/state (404 = none
    // registered), register is 409-speaking when one exists (remove first).
    // The GET has two faces (#157 LC1): the full License for cluster-root
    // authority, the reduced LicenseStatus (state + expiresAt, no productKey)
    // for any other license.read holder — distinguish by `productKey`.
    const licensing = {
        get: async (): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/license"), zGetLicenseResponse),

        register: async (body: Record<string, unknown>): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/license", { body: body as never }), zLicense),

        remove: async (): Promise<void> => {
            unwrap(await transport.DELETE("/api/v6/license"));
        },
    };



    const auth = {
        // Step 1 of the identifier-first login (round #102; #315, 2026-08-09): the
        // answer is the NEXT STEP — never WHETHER the account exists, and never WHICH
        // REALM resolved the identifier. Needs no authentication at all.
        identify: async (username: string): Promise<IdentifyResult> =>
            unwrap(await transport.POST("/api/v6/auth/identify", { body: { username } }), zIdentifyResult),

        // realm defaults to `local` server-side; on cluster members the cluster-named
        // realm authenticates against the cluster store (revised 2026-07-14). `otp`
        // is the second factor, sent once the server asked for it (401 mfa_required):
        // a TOTP code or an unused recovery code (round #102).
        login: async (username: string, password: string, realm?: string, otp?: string): Promise<SessionCreated> => {
            const session = unwrap<SessionCreated>(
                await transport.POST("/api/v6/auth/sessions", {
                    body: {
                        username,
                        password,
                        ...(realm !== undefined ? { realm } : {}),
                        ...(otp !== undefined && otp !== "" ? { otp } : {}),
                    },
                }),
                zSessionCreated,
            );
            token = session.token;
            return session;
        },

        logout: async (): Promise<void> => {
            unwrap(await transport.DELETE("/api/v6/auth/sessions/current"));
            token = null;
            sudoToken = null;
        },

        // Sudo elevation (§3): the tvsu_ grant rides every following request.
        sudo: async (password: string): Promise<void> => {
            const grant = unwrap<Record<string, unknown>>(await transport.POST("/api/v6/auth/sudo", { body: { password } }));
            sudoToken = String(grant["sudoToken"]);
        },

        setToken: (value: string | null): void => {
            token = value;
        },

        // Permission introspection (#152): the catalog subset the calling credential
        // effectively holds on ONE scope (path string or Scope id, no `/*`), as plain
        // names. Unknown or out-of-reach scopes answer [], indistinguishable from
        // holding nothing — clients disable actions up front instead of harvesting
        // 403s.
        //
        // Call it WITHOUT a scope for «anywhere» (round gate-eventi): the union over
        // every selector the caller reaches, which is what decides 403-or-not on a
        // guarded endpoint. That is the question a surface with no row — a navigation
        // entry, a federation-level page — has to ask.
        myPermissions: async (scope?: string): Promise<string[]> => {
            const body = unwrap<{ items: { name: string }[] }>(
                await transport.GET("/api/v6/auth/me/permissions", {
                    params: { query: (scope !== undefined ? { scope } : {}) as never },
                }),
                zPermissionList,
            );
            return body.items.map((entry) => entry.name);
        },

        // Scoped sessions (Round C, ratifiche 2026-07-28 #24/#25): one's OWN sessions
        // answer on authentication alone; another principal's only while the caller
        // holds user.sessions.revoke on the OWNER's scope — outside that reach an id
        // reads as 404. `get` is the native SessionIsActive (`active` is the payload,
        // not a precondition); `delete` is the scoped revoke (on an own session, a
        // logout by id). The list carries only live sessions of visible principals.
        sessions: {
            list: async (p: Pick<ListParams, "cursor"> = {}): Promise<Page<Record<string, unknown>>> =>
                page(await transport.GET("/api/v6/auth/sessions", { params: { query: listQuery(p) as never } }), zSession),

            get: async (id: string): Promise<Record<string, unknown>> =>
                unwrap(await transport.GET("/api/v6/auth/sessions/{session}", { params: { path: { session: id } } }), zSession),

            delete: async (id: string): Promise<void> => {
                unwrap(await transport.DELETE("/api/v6/auth/sessions/{session}", { params: { path: { session: id } } }));
            },
        },

        // External authentication realms (round #102, hoisted here by round #170).
        // Federation-level config, never scoped; every write is sudo-protected — a
        // realm is a door into the platform.
        realms: {
            list: async (p: Omit<ListParams<FilterableFields["listAuthRealms"]>, "scope"> = {}): Promise<Page<AuthRealm>> =>
                page(await transport.GET("/api/v6/auth/realms", { params: { query: listQuery(p) as never } }), zAuthRealmRendered),

            listAll: (p: Omit<ListParams<FilterableFields["listAuthRealms"]>, "scope" | "cursor"> = {}): PageIterator<AuthRealm> =>
                genPageIterator(async ({ cursor }) => auth.realms.list({ ...p, cursor })),

            get: async (id: string): Promise<AuthRealm> =>
                unwrap(await transport.GET("/api/v6/auth/realms/{realm}", { params: { path: { realm: id } } }), zAuthRealmRendered),

            create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<AuthRealm> =>
                unwrap(await transport.POST("/api/v6/auth/realms", { params: { query: writeQuery(opts) as never }, body: body as never }), zAuthRealmRendered),

            patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<AuthRealm> =>
                unwrap(await transport.PATCH("/api/v6/auth/realms/{realm}", {
                    params: { path: { realm: id }, query: writeQuery(opts) as never },
                    body: mergePatch as never,
                    headers: mergePatchHeaders(opts),
                }), zAuthRealmRendered),

            delete: async (id: string): Promise<void> => {
                unwrap(await transport.DELETE("/api/v6/auth/realms/{realm}", { params: { path: { realm: id } } }));
            },

            // A configuration probe, never an authentication: it answers 200 with its
            // verdict even when the directory refuses.
            test: async (id: string, username: string | null): Promise<AuthRealmTestResult> =>
                unwrap(await transport.POST("/api/v6/auth/realms/{realm}/actions/test", {
                    params: { path: { realm: id } },
                    body: (username !== null && username !== "" ? { username } : {}) as never,
                }), zAuthRealmTestResult),
        },

        // The second factor behind its agnostic interface (round #102, hoisted here
        // by round #170). `myself`/`startTotp`/`confirmTotp` speak for the calling
        // account; `ofUser`/`resetUser` are the administrator's view. removeTotp,
        // resetUser and regenerateRecoveryCodes are sudo-protected.
        mfa: {
            methods: async (): Promise<MfaMethod[]> => {
                const body = unwrap(await transport.GET("/api/v6/auth/mfa/methods"), zMfaMethodList);
                return body.items;
            },

            myself: async (): Promise<MfaStatus> =>
                unwrap(await transport.GET("/api/v6/users/myself/mfa"), zMfaStatus),

            ofUser: async (userId: string): Promise<MfaStatus> =>
                unwrap(await transport.GET("/api/v6/users/{user}/mfa", { params: { path: { user: userId } } }), zMfaStatus),

            // Mints a PENDING enrollment: secret, otpauth:// URI and the
            // server-rendered QR (a self-contained <svg>). Nothing accepts codes
            // until confirmTotp proves the secret was really scanned.
            startTotp: async (): Promise<TotpEnrollment> =>
                unwrap(await transport.POST("/api/v6/users/myself/mfa/totp"), zTotpEnrollment),

            // Confirms the pending enrollment and answers the recovery codes —
            // shown exactly once.
            confirmTotp: async (code: string): Promise<string[]> => {
                const body = unwrap(await transport.POST("/api/v6/users/myself/mfa/totp/actions/confirm", { body: { code } }), zRecoveryCodes);
                return body.codes;
            },

            removeTotp: async (): Promise<void> => {
                unwrap(await transport.DELETE("/api/v6/users/myself/mfa/totp"));
            },

            resetUser: async (userId: string): Promise<void> => {
                unwrap(await transport.DELETE("/api/v6/users/{user}/mfa", { params: { path: { user: userId } } }));
            },

            regenerateRecoveryCodes: async (): Promise<string[]> => {
                const body = unwrap(await transport.POST("/api/v6/users/myself/mfa/recoveryCodes/actions/regenerate"), zRecoveryCodes);
                return body.codes;
            },

            // The enrollment COLLECTION (round #178 half B): every subject that
            // holds (or is midway through) a second factor — managed accounts and
            // directory identities in ONE list, keyed by enrollment id (UUIDv7).
            // `delete` is the subject-agnostic administrative reset, sudo-protected;
            // on an ORPHANED row it is the only thing left to do.
            enrollments: {
                list: async (p: Omit<ListParams<FilterableFields["listMfaEnrollments"]>, "scope"> = {}): Promise<Page<MfaEnrollment>> =>
                    page(await transport.GET("/api/v6/auth/mfa/enrollments", { params: { query: listQuery(p) as never } }), zMfaEnrollment),

                listAll: (p: Omit<ListParams<FilterableFields["listMfaEnrollments"]>, "scope" | "cursor"> = {}): PageIterator<MfaEnrollment> =>
                    genPageIterator(async ({ cursor }) => auth.mfa.enrollments.list({ ...p, cursor })),

                get: async (id: string): Promise<MfaEnrollment> =>
                    unwrap(await transport.GET("/api/v6/auth/mfa/enrollments/{enrollment}", { params: { path: { enrollment: id } } }), zMfaEnrollment),

                delete: async (id: string): Promise<void> => {
                    unwrap(await transport.DELETE("/api/v6/auth/mfa/enrollments/{enrollment}", { params: { path: { enrollment: id } } }));
                },
            },
        },
    };



    // Catalogs (Annex U, rewritten by round K1): the HOME of files — a named,
    // scoped container whose contents live at /api/v6/catalogFiles (filter
    // catalog:eq:"<id>"); the render carries only the per-kind `files` counts.
    // Pure config CRUD — delete refuses 409 in_use while the catalog holds
    // files; the item surface died with the round (the contents ARE the files).
    const catalogs = {
        list: async (p: ListParams<FilterableFields["listCatalogs"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/catalogs", { params: { query: listQuery(p) as never } }), zCatalog),

        listAll: (p: Omit<ListParams<FilterableFields["listCatalogs"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => catalogs.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/catalogs/{catalog}", { params: { path: { catalog: id } } }), zCatalog),

        create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/catalogs", { params: { query: writeQuery(opts) as never }, body: body as never }), zCatalog),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/catalogs/{catalog}", {
                params: { path: { catalog: id }, query: writeQuery(opts) as never },
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zCatalog),

        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v6/catalogs/{catalog}", { params: { path: { catalog: id } } }));
        },

        batchDelete: async (ids: string[]): Promise<BatchDeleteResult> =>
            unwrap(await transport.DELETE("/api/v6/catalogs/batch", { body: { ids } as never }), zBatchDeleteResult),
    };



    // Tags (Annex T): the vocabulary plus the GENERIC assignment surface — one
    // call tags anything, since the kind is a field and not an endpoint. `tag`
    // in an assignment body is a REFERENCE (bare name, /scope/name, or uuid):
    // the backend resolves it against the caller's visible set, so the bare-name
    // rule lives in one place for every client.
    const tags = {
        list: async (p: ListParams<FilterableFields["listTags"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/tags", { params: { query: listQuery(p) as never } }), zTag),

        listAll: (p: Omit<ListParams<FilterableFields["listTags"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => tags.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/tags/{tag}", { params: { path: { tag: id } } }), zTag),

        create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/tags", { params: { query: writeQuery(opts) as never }, body: body as never }), zTag),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/tags/{tag}", {
                params: { path: { tag: id }, query: writeQuery(opts) as never },
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zTag),

        // `cascade` (ratified 2026-07-28) deletes the values and every assignment
        // along with the tag — the vCenter category-delete semantics VMWCompat
        // projects; without it a carried tag refuses 409 in_use.
        delete: async (id: string, opts: { cascade?: boolean } = {}): Promise<void> => {
            unwrap(await transport.DELETE("/api/v6/tags/{tag}", {
                params: {
                    path: { tag: id },
                    ...(opts.cascade === true ? { query: { cascade: true } } : {}),
                },
            }));
        },

        batchDelete: async (ids: string[]): Promise<BatchDeleteResult> =>
            unwrap(await transport.DELETE("/api/v6/tags/batch", { body: { ids } as never }), zBatchDeleteResult),

        addValue: async (id: string, value: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/tags/{tag}/values", {
                params: { path: { tag: id } },
                body: { value } as never,
            }), zTag),

        // The value keeps its identity: every assignment follows the rename.
        renameValue: async (id: string, value: string, newValue: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/tags/{tag}/values/{value}", {
                params: { path: { tag: id, value } },
                body: { value: newValue } as never,
                headers: { "content-type": "application/merge-patch+json" },
            }), zTag),

        deleteValue: async (id: string, value: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v6/tags/{tag}/values/{value}", {
                params: { path: { tag: id, value } },
            }));
        },

        assignments: {
            list: async (p: ListParams<FilterableFields["listTagAssignments"]> = {}): Promise<Page<Record<string, unknown>>> =>
                page(await transport.GET("/api/v6/tag-assignments", { params: { query: listQuery(p) as never } }), zTagAssignment),

            listAll: (p: Omit<ListParams<FilterableFields["listTagAssignments"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
                genPageIterator(async ({ cursor }) => tags.assignments.list({ ...p, cursor })),

            add: async (body: {
                kind: string;
                resource: string;
                tag: string;
                value: string;
                overwrite?: boolean;
            }, opts: WriteOpts = {}): Promise<Record<string, unknown>> =>
                unwrap(await transport.POST("/api/v6/tag-assignments", { params: { query: writeQuery(opts) as never }, body: body as never }), zTagAssignment),

            remove: async (id: string): Promise<void> => {
                unwrap(await transport.DELETE("/api/v6/tag-assignments/{assignment}", {
                    params: { path: { assignment: id } },
                }));
            },
        },
    };



    // Firewall groups (#168 — the annex is firewall.yaml): the tags model
    // applied to packet rules — a scoped vocabulary of rules, referenced from
    // guest NICs by NAME with the tag-reference grammar (the SERVER resolves a
    // NIC's reference, like a tag assignment's). Pure config CRUD, no task:
    // each node's worker converges the affected chains from its replica.
    // Delete refuses 409 in_use while any guest NIC references the group — no
    // cascade here, on purpose (ratified: deleting a referenced group would
    // silently open traffic on every referencing NIC).
    const firewallGroups = {
        list: async (p: ListParams<FilterableFields["listFirewallGroups"]> = {}): Promise<Page<Record<string, unknown>>> =>
            page(await transport.GET("/api/v6/firewallGroups", { params: { query: listQuery(p) as never } }), zFirewallGroup),

        listAll: (p: Omit<ListParams<FilterableFields["listFirewallGroups"]>, "cursor"> = {}): PageIterator<Record<string, unknown>> =>
            genPageIterator(async ({ cursor }) => firewallGroups.list({ ...p, cursor })),

        get: async (id: string): Promise<Record<string, unknown>> =>
            unwrap(await transport.GET("/api/v6/firewallGroups/{firewallGroup}", { params: { path: { firewallGroup: id } } }), zFirewallGroup),

        create: async (body: Record<string, unknown>, opts: WriteOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.POST("/api/v6/firewallGroups", { params: { query: writeQuery(opts) as never }, body: body as never }), zFirewallGroup),

        patch: async (id: string, mergePatch: Record<string, unknown>, opts: PatchOpts = {}): Promise<Record<string, unknown>> =>
            unwrap(await transport.PATCH("/api/v6/firewallGroups/{firewallGroup}", {
                params: { path: { firewallGroup: id }, query: writeQuery(opts) as never },
                body: mergePatch as never,
                headers: mergePatchHeaders(opts),
            }), zFirewallGroup),

        delete: async (id: string): Promise<void> => {
            unwrap(await transport.DELETE("/api/v6/firewallGroups/{firewallGroup}", { params: { path: { firewallGroup: id } } }));
        },
    };



    return { auth, system, licensing, vms, storages, credentials, ociRegistries, ociRepositories, ociBranches, ociImages, ociViewClaims, pods, apps, backupDestinations, checkpoints, backup, vmBackups, ctBackups, appBackups, hci, disks, catalogFiles, catalogs, cts, fabrics, firewallGroups, subnets, ipam, networkServices, overlayNetworks, users, groups, scopes, tags, rbac, cluster, proximity, tasks, events, audit, notificationTargets, notificationRules, notificationDeliveries };
}
